/**
 * DivePlanner.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { Buhlmann } from './Buhlmann.js'
import { Gas } from './Gas.js'
import { CCR } from './CCR.js'
import { PlanError, PLAN_ERROR_CODES, PLAN_WARNING_CODES } from './errors.js'
import { debugPlanner, debugPlannerDeco, debugPlannerSegments } from '../utils/debug.js'

// What is left in the cylinders as the ascent is being built.
//
// The engine used to choose a mix on its oxygen fraction alone, so the richest
// mix reaching the depth took the whole ascent however small its cylinder was.
// On the 70 m CCR plan this comes from, a 7 L / 200 bar of TX 17/37 was asked
// for 3865 L while a 12 L / 300 bar of TX14/48 sat untouched beside it: the
// sheet declared the dive unfeasible although the diver carried 5000 L of
// bottom gas.
//
// The budget is only kept for cylinders the diver actually described. A gas
// without tankVolume / tankPressure is unlimited here, which is what keeps the
// 324 reference scenarios - none of which carries a cylinder - byte for byte
// identical.
class GasBudget {
  // A cylinder is tied to the mix it holds, not to the name it was given: two
  // cylinders of the same trimix feed one another, whichever is in the mouth.
  static mixKey(gas) {
    return `${Number(gas.fO2 || 0).toFixed(4)}/${Number(gas.fHe || 0).toFixed(4)}`
  }

  constructor(gases, { rmvDeco, rmvStress = null, stressUntil = 'first_stop', environment = {} } = {}) {
    this.environment = environment
    this.rmvDeco = rmvDeco
    this.rmvStress = rmvStress
    this.stressUntil = stressUntil
    this.capacity = new Map()
    this.tracked = false

    for (const gas of (Array.isArray(gases) ? gases : [])) {
      if (!Number.isFinite(gas.tankVolume) || !Number.isFinite(gas.tankPressure)) continue
      if (gas.tankVolume <= 0 || gas.tankPressure <= 0) continue
      const key = GasBudget.mixKey(gas)
      this.capacity.set(key, (this.capacity.get(key) || 0) + gas.tankVolume * gas.tankPressure)
      this.tracked = true
    }

    this.spent = new Map()
    this.initialGasName = null
    this.inStress = rmvStress !== null
    this.exhausted = false
    // Mixes already reported as run dry, by mixKey. A cylinder is emptied once
    // but the pass comes back to the depth it died at, so without this the same
    // switch would be announced twice at two tenths of a metre apart.
    this.announced = new Set()
    this.baseline = null
  }

  // Freeze the state reached before the decompression passes, so every replay
  // of resolveGfReference starts from the same cylinders. Without it five
  // passes would spend the gas five times and empty every cylinder on their
  // own.
  markBaseline() {
    this.baseline = {
      spent: new Map(this.spent),
      inStress: this.inStress,
      exhausted: this.exhausted,
      announced: new Set(this.announced),
      initialGasName: this.initialGasName
    }
  }

  rewind() {
    if (!this.baseline) return
    this.spent = new Map(this.baseline.spent)
    this.inStress = this.baseline.inStress
    this.exhausted = this.baseline.exhausted
    this.announced = new Set(this.baseline.announced)
    this.initialGasName = this.baseline.initialGasName
  }

  remaining(gas) {
    if (!this.tracked) return Infinity
    const key = GasBudget.mixKey(gas)
    if (!this.capacity.has(key)) return Infinity
    return this.capacity.get(key) - (this.spent.get(key) || 0)
  }

  hasGasLeft(gas) {
    return this.remaining(gas) > 0
  }

  // The RMV a segment is breathed at, without touching the state: the stress
  // phase of a bailout ends on the very event calculateGasConsumption uses, so
  // the gas the ascent is planned on is the gas the requirement table prints.
  rmvFor(segment) {
    if (!this.inStress) return this.rmvDeco
    if (this.stressUntil === 'first_stop' && segment.type === 'stop') return this.rmvDeco
    if (this.stressUntil === 'gas_switch' && this.initialGasName !== null && segment.gas !== this.initialGasName) {
      return this.rmvDeco
    }
    return this.rmvStress
  }

  // Litres a segment costs - the very formula charge() then spends, so a
  // segment can be measured before it is produced.
  costOf(segment) {
    if (!this.tracked) return 0
    return DivePlanner.litersFor(segment, this.rmvFor(segment), this.environment)
  }

  // How long a mix can be breathed at a depth before its cylinders are empty.
  // Infinity when nothing limits it, 0 when there is nothing left.
  sustainableTime(gas, segment) {
    const left = this.remaining(gas)
    if (!Number.isFinite(left)) return Infinity
    const perMinute = this.costOf({ ...segment, time: 1 })
    if (!(perMinute > 0)) return Infinity
    return Math.max(0, left / perMinute)
  }

  // Mark a mix as empty. Used when what is left cannot pay for a measurable
  // slice of the segment ahead: without it the pass could keep coming back to
  // the same cylinder for a few millilitres and never move.
  drain(gas) {
    const key = GasBudget.mixKey(gas)
    if (!this.capacity.has(key)) return
    this.spent.set(key, this.capacity.get(key))
  }

  charge(segment) {
    if (!this.tracked || !segment || !(segment.time > 0)) return
    if (this.initialGasName === null && segment.gas) this.initialGasName = segment.gas
    const key = GasBudget.mixKey(segment)
    if (segment.fO2 === undefined || !this.capacity.has(key)) {
      this.endStressPhase(segment)
      return
    }
    const liters = this.costOf(segment)
    this.endStressPhase(segment)
    if (!(liters > 0)) return
    this.spent.set(key, (this.spent.get(key) || 0) + liters)
  }

  endStressPhase(segment) {
    if (!this.inStress || this.stressUntil === 'surface') return
    if (this.stressUntil === 'first_stop' && segment.type === 'stop') this.inStress = false
    if (this.stressUntil === 'gas_switch' && this.initialGasName !== null && segment.gas !== this.initialGasName) {
      this.inStress = false
    }
  }
}

// Dive planner - computes decompression schedule for OC and CCR
//
// Every depth <-> pressure conversion goes through Gas.pressureAtDepth /
// Gas.depthAtPressure with this.environment, so altitude and water type are
// applied consistently from tissue loading down to gas consumption.
export class DivePlanner {
  // Longest single deco stop evaluated by findStopTime, in minutes.
  // Reaching it means the ceiling did not clear: the plan is truncated and a
  // PLAN_WARNING_CODES.STOP_TIME_TRUNCATED warning is reported.
  static MAX_STOP_TIME = 120

  // Longest runtime the deco loops will compute, in minutes.
  // Reaching it interrupts the loop and reports PLAN_WARNING_CODES.RUNTIME_LIMIT.
  static MAX_RUNTIME = 999

  // Surface intervals recommended by DAN before flying. The consensus has three
  // cases and exactly three, quoted here verbatim from the DAN page "Guidelines
  // for Flying After Diving", which reports the consensus of the Flying After
  // Recreational Diving Workshop (2002):
  //
  //   "A minimum of 12-hour surface interval was recommended for the single
  //    no-decompression dive."
  //   "A minimum of 18-hour surface interval for multi-day repetitive diving."
  //   "Substantially longer than 18 hours after diving involving compulsory
  //    decompression, or using heliox and trimix."
  //
  // The third case therefore has NO published figure: 18 h is a floor DAN
  // explicitly asks to exceed, which is why it is reported through
  // result.noFlyMinimum rather than as a delay of its own. Printing a bare 18 h
  // for a decompression dive would put in the diver's hands a number DAN never
  // gave, on the side that is not safe. Helium is the other half of that same
  // third case and gets its own reason, so the sentence under the plan can name
  // what triggered it.
  //
  // This is a published recommendation, not a computation, and it is reported
  // as such. Running the Buhlmann model against an airliner cabin pressure was
  // tried and dropped: it returns one to two hours on the very same profiles,
  // because GF High governs a few minutes of ascent and not a multi-hour
  // altitude exposure. Displaying that number would have been a dangerous
  // piece of advice dressed up as physiology.
  static NO_FLY_NO_STOP = 12 * 60
  static NO_FLY_REPETITIVE = 18 * 60
  static NO_FLY_LONGER = 18 * 60

  // Reasons reported in result.noFlyReason, so the UI can name the DAN case it
  // is showing instead of displaying a bare number of hours. DECO and HELIUM
  // are the two halves of the third case and both report a floor.
  static NO_FLY_REASONS = Object.freeze({
    NO_STOP: 'no_stop',
    REPETITIVE: 'repetitive',
    DECO: 'deco',
    HELIUM: 'helium'
  })

  // Delay reported for a DAN case, in minutes. An unknown reason falls back on
  // the longest published delay and says so: reporting 12 h for a case this
  // planner does not recognize would be the one error that cannot be caught by
  // reading the screen.
  static noFlyDelay(reason) {
    switch (reason) {
      case DivePlanner.NO_FLY_REASONS.NO_STOP:
        return DivePlanner.NO_FLY_NO_STOP
      case DivePlanner.NO_FLY_REASONS.REPETITIVE:
        return DivePlanner.NO_FLY_REPETITIVE
      case DivePlanner.NO_FLY_REASONS.DECO:
      case DivePlanner.NO_FLY_REASONS.HELIUM:
        return DivePlanner.NO_FLY_LONGER
      default:
        console.error('noFlyDelay: unknown DAN case (%o), reporting the longest published delay', reason)
        return DivePlanner.NO_FLY_LONGER
    }
  }

  // Whether a dive breathed a helium mix, read off the segments it produced.
  //
  // Every segment holding breathing time carries the fraction of helium of what
  // was actually breathed - the loop mix on a rebreather, the bottle on open
  // circuit - so this reports the mix the diver had in his lungs and not the one
  // he configured: a trimix bottle the plan never reaches does not make the dive
  // a trimix dive. Zero duration markers (gasChange, diluentChange) carry no
  // fraction at all and are skipped by the same test.
  static usesHelium(segments) {
    if (!Array.isArray(segments)) {
      console.error('usesHelium: the plan carries no segment list (segments=%o), reporting no helium', segments)
      return false
    }
    return segments.some(s => Number.isFinite(s.fHe) && s.fHe > 0)
  }

  // Exposure a first dive of the day starts from: clean tissues, no oxygen
  // clock running. Frozen because it is shared by every planner instance that
  // never received a previous dive.
  static NO_PREVIOUS_DIVE = Object.freeze({
    cns: 0,
    otu: 0,
    surfaceInterval: null,
    repetitive: false,
    unloadedState: false,
    helium: false
  })

  // Tolerance, in metres, when a ceiling is compared to a depth the diver
  // holds. Both are floating point results - the ceiling comes from tissue
  // pressures through several divisions and a water column conversion, the
  // depth from rate * time sums - so a ceiling equal to the depth lands a few
  // 1e-13 m either side of it. Below that tolerance a level counts as
  // reachable and no decompression is owed to join it.
  //
  // src/utils/alerts.js imports this very constant for its ceiling violation
  // alert: a plan the engine considers legal must not be flagged by the UI,
  // and a plan the UI flags must be one the engine refused to produce.
  static CEILING_TOLERANCE_M = 0.1

  // Shortest slice of a segment worth planning on a cylinder about to run dry,
  // in minutes. Below it the cylinder is treated as empty: splitting a stop
  // into a six second sliver would add a gas switch nobody performs, and could
  // keep the pass returning to the same depth without ever moving.
  static MIN_BUDGETED_SEGMENT_TIME = 0.5

  // Most steps a single decompression pass may take before it is declared
  // stuck. A 999 min ascent on a 3 m grid from 200 m needs well under 200
  // steps, gas switches included.
  static MAX_DECO_STEPS = 2000

  // Cylinder assumed when the diver declared none. Only the requirement table
  // uses these: the gas the ascent switches to is never decided on a cylinder
  // nobody described, so a plan made of gases without volumes behaves exactly
  // as it always did.
  static DEFAULT_TANK_VOLUME = 12
  static DEFAULT_TANK_PRESSURE = 200

  // Share of a cylinder the plan has to leave untouched for its line to read as
  // comfortable rather than tight. A reporting threshold and nothing else: the
  // engine keeps planning down to the last litre and says so, but 'OK, 1 L
  // left' on a 12 L / 280 bar and 'OK, 1126 L left' on a 6.8 L are not the same
  // statement, and the table printed both in green.
  static GAS_RESERVE_FRACTION = 0.1

  // Lowest oxygen partial pressure a planned mix may be breathed at, in bar.
  //
  // Deliberately above the 0.16 bar at which alerts.js raises a hypoxia error:
  // 0.16 is where a diver is about to black out, 0.18 is the accepted floor for
  // planning. The margin between the two is exactly what the flush warning is
  // meant to catch - a hypoxic bottom diluent flushed too shallow lands there,
  // and no other check would say a word about it.
  static HYPOXIC_PPO2 = 0.18

  constructor(config = {}) {
    this.algorithm = config.algorithm || Buhlmann.ALGORITHMS.ZHL16C
    // Dive environment { altitude: <metres>, waterType: <preset key or density
    // in g/l> }, both optional. Defaults are sea level and salt water, i.e. a
    // surface pressure of 1.01325 bar and 0.101043 bar per metre (see Gas.js).
    this.environment = config.environment || {}
    this.model = new Buhlmann(this.algorithm, this.environment)
    // Exposure carried over from the previous dive of the series, replaced by
    // applyPreviousDive at the start of every plan. planBailout reads it back:
    // it runs after planCCR on the same planner and inherits its series.
    this.carriedOver = DivePlanner.NO_PREVIOUS_DIVE
    // What is left in the cylinders while an ascent is being built, or null
    // when the diver described none. Set up by the plan entry points and torn
    // down with them, never read across two plans.
    this.gasBudget = null
    // `??` and not `||` on every physical setting: the default must be applied
    // when the caller provides nothing, never to cover up a 0 or a NaN (an
    // emptied settings field parses to NaN). Those values reach validateConfig
    // and are refused instead of being silently replaced.
    this.ascentRate = config.ascentRate ?? 9    // m/min
    this.descentRate = config.descentRate ?? 20 // m/min
    this.lastStopAscentRate = config.lastStopAscentRate ?? 3 // m/min (last stop to surface)
    this.decoAscentRate = config.decoAscentRate ?? 3 // m/min (between deco stops)
    this.decoStopInterval = config.decoStopInterval ?? 3 // meters between stops
    this.lastStopDepth = config.lastStopDepth ?? 3 // final stop depth
    this.ppO2Max = config.ppO2Max ?? 1.4 // working ppO2
    this.ppO2Deco = config.ppO2Deco ?? 1.6 // deco ppO2
    this.gasSwitchAtStop = config.gasSwitchAtStop || false // false = switch ASAP at MOD
    this.gasSwitchDuration = config.gasSwitchDuration ?? 2 // minutes per gas switch, 2 min by default, same value as Store.js (two divers, first check, switch gas, breath ok, change computer settings, then second diver same process)
    // Minutes held at the last stop when the ascent owes no decompression stop
    // at all, written as a stop flagged `safety`. 0 here: the engine plans the
    // decompression and nothing else, the application carries its own default
    // (Store, settings.safetyStopTime). This replaces the minute every plan
    // used to end with at lastStopDepth, which was printed as a decompression
    // stop on dives that owed none.
    this.safetyStopTime = config.safetyStopTime ?? 0
    this.validateConfig()
  }

  // Validate the physical settings of the planner itself.
  // A negative or non finite rate is not a cosmetic problem: durations are
  // computed as distance / rate, so a negative rate gives negative segment
  // times, a shorter runtime and a plan that looks less demanding than the
  // real dive. Every rejection logs the faulty value before throwing.
  validateConfig() {
    const positive = [
      ['ascentRate', this.ascentRate],
      ['descentRate', this.descentRate],
      ['lastStopAscentRate', this.lastStopAscentRate],
      ['decoAscentRate', this.decoAscentRate],
      ['lastStopDepth', this.lastStopDepth],
      ['decoStopInterval', this.decoStopInterval],
      ['ppO2Max', this.ppO2Max],
      ['ppO2Deco', this.ppO2Deco]
    ]

    for (const [name, value] of positive) {
      if (!Number.isFinite(value) || value <= 0) {
        console.error('DivePlanner: invalid %s (%o), expected a finite value > 0', name, value)
        throw new PlanError(PLAN_ERROR_CODES.INVALID_CONFIG, { context: 'DivePlanner', setting: name, value })
      }
    }

    if (!Number.isFinite(this.safetyStopTime) || this.safetyStopTime < 0) {
      console.error('DivePlanner: invalid safetyStopTime (%o), expected a finite value >= 0', this.safetyStopTime)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_CONFIG, { context: 'DivePlanner', setting: 'safetyStopTime', value: this.safetyStopTime })
    }

    // A gas switch may legitimately be instantaneous (0 min), never negative
    if (!Number.isFinite(this.gasSwitchDuration) || this.gasSwitchDuration < 0) {
      console.error('DivePlanner: invalid gasSwitchDuration (%o), expected a finite value >= 0', this.gasSwitchDuration)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_CONFIG, {
        context: 'DivePlanner', setting: 'gasSwitchDuration', value: this.gasSwitchDuration
      })
    }

    // Water density: same treatment as the settings above, same error code.
    // It is resolved through the very function every depth <-> pressure
    // conversion of the plan calls, so an unusable salinity is refused at
    // construction instead of being absorbed into a plan computed for another
    // water. Gas.waterDensity logs the faulty value before throwing.
    const density = Gas.waterDensity(this.environment.waterType)
    debugPlanner('DivePlanner: environment altitude=%om, water density=%o g/l (%o bar/m)',
      this.environment.altitude || 0, density, Gas.specificWeight(this.environment.waterType))
  }

  // Litres of gas a segment costs at the surface: RMV times its minutes times
  // the ambient pressure at the depth it is breathed at - the single depth of
  // a stop, a bottom or a gas change, the mean depth of a transition. The one
  // formula the cylinder budget, the open circuit table and the bailout table
  // all read, so the gas an ascent is planned on is the gas its table prints.
  // Zero for a segment with no breathing time, an unusable depth or an
  // unusable RMV.
  static litersFor(segment, rmv, environment = {}) {
    if (!segment || !(segment.time > 0) || !Number.isFinite(rmv)) return 0
    const depth = (segment.type === 'stop' || segment.type === 'bottom' || segment.type === 'gasChange')
      ? segment.depth
      : (segment.startDepth + segment.endDepth) / 2
    if (!Number.isFinite(depth)) return 0
    return rmv * segment.time * Gas.pressureAtDepth(depth, environment)
  }

  // Load tissues at constant depth through the finiteness guard.
  // Buhlmann.loadTissues multiplies the fractions by the ambient pressure
  // without checking them, so a single NaN fraction produces a plan with no
  // decompression stop at all and no error whatsoever.
  loadTissuesGuarded(model, depth, time, gas, context) {
    Gas.assertBreathingMix(gas, context)
    model.loadTissues(depth, time, gas)
  }

  // Same guard for the variable pressure (Schreiner) loading
  loadTissuesAscDecGuarded(model, startDepth, endDepth, time, gas, context) {
    Gas.assertBreathingMix(gas, context)
    model.loadTissuesAscDec(startDepth, endDepth, time, gas)
  }

  // Same guard for a rebreather transition, given as the inspired inert
  // pressures at both ends (CCR.calcEquivalentGas). A NaN here would poison
  // the compartments exactly like a NaN fraction does.
  loadTissuesInspiredGuarded(model, start, end, time, context) {
    for (const [label, point] of [['start', start], ['end', end]]) {
      if (!point || !Number.isFinite(point.ppN2) || !Number.isFinite(point.ppHe) || point.ppN2 < 0 || point.ppHe < 0) {
        console.error('%s: refusing to load tissues with an unusable inspired pressure at the %s of the leg (%o)', context, label, point)
        throw new PlanError(PLAN_ERROR_CODES.INVALID_GAS, { context, reason: 'inspired_pressure_not_finite', point: label })
      }
    }
    if (!(time > 0)) return
    model.loadTissuesInspired(start, end, time)
  }

  // Load a rebreather leg from `fromDepth` to `toDepth` over `time` minutes at
  // the setpoint of `phase`.
  //
  // At constant setpoint the inspired inert pressure is linear in depth, so
  // the Schreiner equation integrates the leg exactly - except where the
  // setpoint cannot be held. Above CCR.calcMinDepth(setpoint) the loop ppO2 is
  // the ambient pressure itself and the inert pressure sits at zero: the leg
  // is a flat piece there and a ramp below, so it is loaded as two legs split
  // at that depth, each exact. A single ramp over the whole leg would load the
  // first metres of a descent with inert gas the loop does not hold.
  loadCCRLeg(model, ccr, fromDepth, toDepth, time, phase, context) {
    if (!(time > 0) || fromDepth === toDepth) return
    const setpoint = ccr.getSetpoint(phase)
    const onset = CCR.inertOnsetDepth(setpoint, this.environment)
    const shallow = Math.min(fromDepth, toDepth)
    const deep = Math.max(fromDepth, toDepth)
    const legs = []
    if (onset > shallow && onset < deep) {
      // Split where the loop starts holding inert gas, time pro rata of the
      // distance since the leg runs at a constant rate
      const share = Math.abs(onset - fromDepth) / (deep - shallow)
      legs.push([fromDepth, onset, time * share], [onset, toDepth, time * (1 - share)])
    } else {
      legs.push([fromDepth, toDepth, time])
    }
    for (const [a, b, t] of legs) {
      this.loadTissuesInspiredGuarded(model, ccr.getEquivalentGas(a, phase), ccr.getEquivalentGas(b, phase), t, context)
    }
  }

  // Get ascent rate depending on context:
  // - to surface: lastStopAscentRate
  // - between deco stops: decoAscentRate
  // - free ascent to first stop: ascentRate
  getAscentRate(targetDepth, inDecoZone = false) {
    if (targetDepth === 0) return this.lastStopAscentRate
    if (inDecoZone) return this.decoAscentRate
    return this.ascentRate
  }

  // Set algorithm (ZHL16B or ZHL16C)
  setAlgorithm(algorithm) {
    this.algorithm = algorithm
    this.model.setAlgorithm(algorithm)
  }

  // Set the dive environment (altitude / water type) for the planner and its
  // decompression model. Tissue state is preserved by Buhlmann.setEnvironment.
  //
  // The water density is validated first, with the same code as the
  // constructor: an unusable salinity is refused before it can reach the model,
  // and the planner keeps the environment it was already working with rather
  // than switching to a half-applied one. Gas.waterDensity logs then throws.
  setEnvironment(env) {
    const next = env || {}
    const density = Gas.waterDensity(next.waterType)
    this.environment = next
    this.model.setEnvironment(this.environment)
    debugPlanner('setEnvironment: altitude=%om, water density=%o g/l (%o bar/m)',
      this.environment.altitude || 0, density, Gas.specificWeight(this.environment.waterType))
  }

  // Validate the dive segments of a plan request.
  // Rejects an empty list, a non finite or negative depth, a non finite or
  // negative time. Every rejection logs the faulty value before throwing.
  validateSegments(segments, context) {
    if (!Array.isArray(segments) || segments.length === 0) {
      console.error('%s: no dive segment to plan (segments=%o)', context, segments)
      throw new PlanError(PLAN_ERROR_CODES.NO_SEGMENTS, { context, segments })
    }

    segments.forEach((segment, index) => {
      if (!segment || typeof segment !== 'object') {
        console.error('%s: segment #%d is not an object (%o)', context, index, segment)
        throw new PlanError(PLAN_ERROR_CODES.NO_SEGMENTS, { context, index, segment })
      }
      if (!Number.isFinite(segment.depth) || segment.depth < 0) {
        console.error('%s: segment #%d has an invalid depth (%o), expected a finite depth >= 0',
          context, index, segment.depth)
        throw new PlanError(PLAN_ERROR_CODES.INVALID_DEPTH, { context, index, depth: segment.depth })
      }
      if (!Number.isFinite(segment.time) || segment.time < 0) {
        console.error('%s: segment #%d (%om) has an invalid time (%o), expected a finite time >= 0',
          context, index, segment.depth, segment.time)
        throw new PlanError(PLAN_ERROR_CODES.INVALID_TIME, { context, index, depth: segment.depth, time: segment.time })
      }
    })
  }

  // Validate a gradient factor pair: both finite in ]0,1] and gfLow <= gfHigh
  validateGradientFactors(gfLow, gfHigh, context) {
    const outOfRange = gf => !Number.isFinite(gf) || gf <= 0 || gf > 1
    if (outOfRange(gfLow) || outOfRange(gfHigh) || gfLow > gfHigh) {
      console.error('%s: invalid gradient factors gfLow=%o gfHigh=%o, expected 0 < gfLow <= gfHigh <= 1',
        context, gfLow, gfHigh)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_GF, { context, gfLow, gfHigh })
    }
  }

  // Validate every gas of a list. Gas.validate logs the faulty mix and throws
  // PlanError(plan_error_invalid_gas) itself.
  validateGases(gases, context) {
    if (!Array.isArray(gases)) {
      console.error('%s: the gas list is not an array (%o)', context, gases)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_GAS, { context, gases, reason: 'gases_not_an_array' })
    }
    gases.forEach(gas => Gas.validate(gas))
  }

  // Validate the CCR part of a plan request: a usable diluent (default and per
  // segment) and, for every dive phase actually used by the plan, a setpoint
  // inside the CCR.MIN_SETPOINT .. CCR.MAX_SETPOINT range announced by the
  // plan_error_invalid_setpoint message.
  validateCCRConfig(ccr, diluent, segments) {
    if (!ccr || typeof ccr.getSetpoint !== 'function') {
      console.error('planCCR: no usable CCR configuration (ccr=%o)', ccr)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_SETPOINT, { context: 'planCCR', reason: 'missing_ccr_config' })
    }

    if (!diluent) {
      console.error('planCCR: no diluent provided (diluent=%o)', diluent)
      throw new PlanError(PLAN_ERROR_CODES.NO_DILUENT, { context: 'planCCR', diluent })
    }
    // Composition errors are reported by Gas.validate as plan_error_invalid_gas
    Gas.validate(diluent)

    segments.forEach((segment, index) => {
      if (segment.diluent === undefined) return
      if (!segment.diluent) {
        console.error('planCCR: segment #%d (%om) has an empty diluent (%o)', index, segment.depth, segment.diluent)
        throw new PlanError(PLAN_ERROR_CODES.NO_DILUENT, { context: 'planCCR', index, depth: segment.depth })
      }
      Gas.validate(segment.diluent)
    })

    // The fixed setpoint is checked even in dynamic mode: getSetpoint falls
    // back to it for any phase the configuration does not cover.
    const setpoints = [['fixed', ccr.fixedSetpoint]]
    for (const phase of Object.values(CCR.PHASES)) {
      setpoints.push([phase, ccr.getSetpoint(phase)])
    }

    for (const [phase, setpoint] of setpoints) {
      if (!Number.isFinite(setpoint) || setpoint < CCR.MIN_SETPOINT || setpoint > CCR.MAX_SETPOINT) {
        console.error('planCCR: invalid %s setpoint (%o), expected a finite value between %o and %o bar',
          phase, setpoint, CCR.MIN_SETPOINT, CCR.MAX_SETPOINT)
        throw new PlanError(PLAN_ERROR_CODES.INVALID_SETPOINT, {
          context: 'planCCR', phase, setpoint, min: CCR.MIN_SETPOINT, max: CCR.MAX_SETPOINT
        })
      }
    }
  }

  // Validate and order the loop flushes of a CCR dive.
  //
  // A flush is a deliberate stop on the way up where the diver empties the loop
  // and refills it with a mix of known composition, most often air once the
  // bottom diluent has become too hypoxic to breathe shallow. It is NOT a dive
  // level: putting it in `segments` would move the TTS reference, which is the
  // end of the last segment, and the sheet would print a time to surface
  // measured from the flush depth instead of from the bottom.
  //
  // Returns the list sorted deepest first, which is the order the ascent meets
  // them in. Refuses rather than repairs: a flush the diver cannot reach, or
  // two flushes fighting over the same metre, is a mistake in the plan, and
  // computing something else than what was asked is how a diver ends up
  // breathing a mix he never chose.
  //
  // `keepMix` says what the loop breathes once the flush is over. False, the
  // default, sends the loop back to the diluent it was on: the flush was a
  // gesture - a cell check, a known mix put in front of the cells - and the
  // diluent the rebreather is plumbed to has not changed. True keeps the flushed
  // mix for the rest of the ascent, which is the deliberate helium purge before
  // the decompression. The two are a different dive above the flush depth, not a
  // different label: the remaining stops are computed on whichever inert gas the
  // loop really holds.
  //
  // Anything other than a strict `true` is read as false and journalled unless
  // it is simply absent, because a plan saved before this option existed has no
  // field at all, and reverting is what its author saw when he saved it.
  //
  // @param {Array} flushes - [{ depth, time, diluent, keepMix }], or null/undefined
  // @param {Array} segments - dive segments, for the maximum depth
  // @returns {Array} normalized flushes, deepest first, possibly empty
  normalizeFlushes(flushes, segments, context = 'planCCR') {
    if (flushes === undefined || flushes === null) return []
    if (!Array.isArray(flushes)) {
      console.error('%s: flushes must be an array, got %o', context, flushes)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_FLUSH, { context, reason: 'not_an_array' })
    }
    if (flushes.length === 0) return []

    const maxDepth = Math.max(...segments.map(s => s.depth))

    const normalized = flushes.map((flush, index) => {
      if (!flush || typeof flush !== 'object') {
        console.error('%s: flush #%d is not an object (%o)', context, index, flush)
        throw new PlanError(PLAN_ERROR_CODES.INVALID_FLUSH, { context, index, reason: 'not_an_object' })
      }
      if (!Number.isFinite(flush.depth) || flush.depth <= 0) {
        console.error('%s: flush #%d has an unusable depth (%o)', context, index, flush.depth)
        throw new PlanError(PLAN_ERROR_CODES.INVALID_FLUSH, { context, index, depth: flush.depth, reason: 'depth' })
      }
      // At or below the bottom the flush is not on the way up, it is a change
      // of bottom diluent, which the per segment diluent already expresses.
      if (flush.depth >= maxDepth) {
        console.error('%s: flush #%d is asked for at %om, at or below the %om bottom: a flush happens on the way up',
          context, index, flush.depth, maxDepth)
        throw new PlanError(PLAN_ERROR_CODES.INVALID_FLUSH, {
          context, index, depth: flush.depth, maxDepth, reason: 'below_bottom'
        })
      }
      if (!Number.isFinite(flush.time) || flush.time <= 0) {
        console.error('%s: flush #%d (%om) has an unusable duration (%o)', context, index, flush.depth, flush.time)
        throw new PlanError(PLAN_ERROR_CODES.INVALID_FLUSH, { context, index, depth: flush.depth, time: flush.time, reason: 'time' })
      }
      if (!flush.diluent) {
        console.error('%s: flush #%d (%om) carries no mix to flush with (%o)', context, index, flush.depth, flush.diluent)
        throw new PlanError(PLAN_ERROR_CODES.NO_DILUENT, { context, index, depth: flush.depth })
      }
      Gas.validate(flush.diluent)
      if (flush.keepMix !== undefined && typeof flush.keepMix !== 'boolean') {
        console.error('%s: flush #%d (%om) has a non boolean keepMix (%o), the loop goes back to its diluent',
          context, index, flush.depth, flush.keepMix)
      }
      return {
        depth: flush.depth,
        time: flush.time,
        keepMix: flush.keepMix === true,
        diluent: CCR.normalizeDiluent(flush.diluent, `${context} flush #${index}`)
      }
    }).sort((a, b) => b.depth - a.depth)

    for (let i = 1; i < normalized.length; i++) {
      if (normalized[i].depth === normalized[i - 1].depth) {
        console.error('%s: two flushes are asked for at %om; one flush replaces the whole loop, a second one at the same depth changes nothing',
          context, normalized[i].depth)
        throw new PlanError(PLAN_ERROR_CODES.INVALID_FLUSH, {
          context, depth: normalized[i].depth, reason: 'duplicate_depth'
        })
      }
    }

    debugPlanner('%s: %d flush(es) planned at %s', context, normalized.length,
      normalized.map(f => `${f.depth}m/${f.time}min on ${f.diluent.name}${f.keepMix ? ' (kept)' : ''}`).join(', '))
    return normalized
  }

  // Warning raised when the final ascent never passes through a planned flush.
  // Happens on a multilevel profile whose last level sits above the flush: the
  // ascent to the surface starts shallower than the depth asked for, so the
  // flush simply has no moment to happen in. The plan stays computable and the
  // diver is told which flush was left out.
  flushNotReachedWarning(flush, ascentStart, context) {
    console.error('%s: the flush planned at %om never happens, the final ascent starts at %om',
      context, flush.depth, ascentStart.toFixed(1))
    return {
      code: PLAN_WARNING_CODES.FLUSH_NOT_REACHED,
      details: {
        context,
        depth: flush.depth,
        gas: flush.diluent.name,
        ascentStart: Math.round(ascentStart * 10) / 10
      }
    }
  }

  // Warning raised when a flush puts the loop outside the diver's own working
  // band of oxygen partial pressure.
  //
  // This is the one moment of a rebreather dive where the setpoint does not
  // hold: the loop is whatever the flush mix gives at that depth. Air at 60 m
  // is 1.49 bar, at 70 m 1.70 bar; a 10/70 bottom diluent flushed at 6 m is
  // 0.16 bar. None of those numbers appears anywhere else in the plan, because
  // every other CCR row reads the setpoint.
  //
  // The band is the diver's: HYPOXIC_PPO2 at the bottom, and at the top the MOD
  // of the flush mix under his deco limit, Gas.modOf(mix, ppO2Deco).
  //
  // Two deliberate choices there. ppO2Deco and not ppO2Max, because a flush
  // always happens on the way up - normalizeFlushes refuses one at or below the
  // bottom - so the phase limit that applies to it is the decompression one.
  // And the MOD rather than a bare comparison of partial pressures, so that the
  // flush is judged exactly like every other gas of the application: Gas.modOf
  // carries MOD_TOLERANCE, the deliberate slack that makes pure oxygen "the 6 m
  // gas" at 1.62 bar rather than a 5.81 m one. Without it the standard oxygen
  // flush of the shallow stops carried a warning on every single plan, for two
  // hundredths of a bar the rest of the application already accepts.
  //
  // Cost, stated: a flush on the bottom diluent at 60 m (air, 1.49 bar) no
  // longer warns, since it stays under a 1.6 bar deco limit.
  flushPpO2Warning(flush, ppO2, context) {
    const limit = Gas.ppO2LimitOf(flush.diluent, this.ppO2Deco)
    const mod = Gas.modOf(flush.diluent, this.ppO2Deco, this.environment)
    if (flush.depth <= mod && ppO2 >= DivePlanner.HYPOXIC_PPO2) return null

    console.error('%s: the %om flush on %s gives a ppO2 of %o bar, outside the %o - %o bar working band (MOD %om)',
      context, flush.depth, flush.diluent.name, ppO2.toFixed(2), DivePlanner.HYPOXIC_PPO2, limit, mod)

    return {
      code: PLAN_WARNING_CODES.FLUSH_PPO2,
      details: {
        context,
        depth: flush.depth,
        gas: flush.diluent.name,
        ppO2: ppO2.toFixed(2),
        min: DivePlanner.HYPOXIC_PPO2.toFixed(2),
        max: Number(limit).toFixed(2)
      }
    }
  }

  // Hold a loop flush at `depth`: breathe the flush mix at ambient pressure for
  // its duration, then hand back the mix the rest of the ascent runs on.
  //
  // The oxygen partial pressure is the one the mix really gives, fO2 * pAmb,
  // and not the setpoint. That is the whole physical content of a flush and the
  // reason it can be dangerous: the loop momentarily leaves the controller.
  //
  // What happens after is the diver's call, carried by flush.keepMix:
  //   - false (the default): the loop goes back to `currentDiluent`. The flush
  //     was a gesture, not a gas change; the rebreather is still plumbed to the
  //     same diluent and the remaining stops are computed on it.
  //   - true: the loop keeps the flushed mix, which is the deliberate purge of
  //     the bottom helium before the decompression. A diluentChange row is
  //     pushed so the ascent table says where the mix changed instead of
  //     silently printing another name from that row on.
  //
  // Two honest limits, deliberate, documented rather than half modelled:
  //   - the return to the setpoint is instant once the flush is over. When the
  //     flush LOWERED the ppO2 that is close to true, the solenoid catches up
  //     in seconds. When it RAISED it - a deep air flush - nothing brings it
  //     back down but metabolism and the ascent, which take minutes, so the
  //     oxygen exposure of a deep flush is understated even here. That is what
  //     flushPpO2Warning is for.
  //   - the flush is counted in minutes, while a real one is counted in loop
  //     volumes: the same minute costs eight times more gas at 60 m than at
  //     6 m. Nothing bills it to a cylinder yet.
  //
  // Returns the mix the loop is left on.
  performFlush(depth, flush, ccr, currentDiluent, gfLow, gfHigh, gfReference, result, context) {
    const mix = flush.diluent
    const ppO2 = Gas.ppO2(mix.fO2, depth, this.environment)

    const ppO2Warning = this.flushPpO2Warning(flush, ppO2, context)
    if (ppO2Warning) result.warnings.push(ppO2Warning)

    // The loop holds the flush mix at its own fractions, exactly like an open
    // circuit cylinder: loadTissues multiplies them by (pAmb - pH2O), which is
    // the inspired pressure. No setpoint, no equivalent gas.
    this.loadTissuesGuarded(this.model, depth, flush.time, mix, `${context} flush`)
    result.runtime += flush.time
    this.accumulateExposure(result, ppO2, flush.time)

    const { tissueInfo, compartments, ceiling } = this.getTissueSnapshot(depth, gfLow, gfHigh, gfReference)
    const keepMix = flush.keepMix === true
    const previousGas = currentDiluent?.name

    result.segments.push({
      type: 'flush',
      depth,
      time: flush.time,
      runtime: result.runtime,
      gas: mix.name,
      previousGas,
      keepMix,
      fO2: mix.fO2,
      fHe: mix.fHe || 0,
      ppO2,
      // Null and not the ascent setpoint: during a flush the loop is not under
      // setpoint control, and printing one next to a ppO2 it does not explain
      // would make the two columns contradict each other.
      setpoint: null,
      end: Gas.calcEND(depth, mix.fO2, mix.fHe || 0, this.environment),
      tissueInfo,
      compartments,
      ceiling
    })

    // The loop is left on the flushed mix only when the diver asked for it. No
    // diluentChange row is written for it: the flush row above IS the change,
    // it already carries the new mix, the one it replaces and the keepMix flag,
    // plus a duration and a ppO2. A second row at 0 min with every column empty
    // repeated it and gave the table one more line to decode underwater. The
    // diluentChange pushed between two segments stays, there nothing else says
    // where the diluent changed.
    const nextDiluent = keepMix ? mix : currentDiluent

    ccr.diluent = nextDiluent
    debugPlannerSegments('%s: loop flushed with %s at %om for %o min, ppO2 %o bar, back on %s',
      context, mix.name, depth, flush.time, ppO2.toFixed(2), nextDiluent?.name)
    return nextDiluent
  }

  // Tissue state a dive starts from, and the oxygen exposure it carries over.
  //
  // Without previousDive the tissues are reset to the surface equilibrium of
  // the dive site, which is what a first dive of the day does and what this
  // planner always did.
  //
  // With one, the dive is a repetitive dive: the tissues left by the previous
  // dive are restored, then off-gassed for the surface interval. previousDive:
  //   tissues         serialized state (Buhlmann.toJSON) or a Buhlmann instance
  //   surfaceInterval minutes spent at the surface, required, >= 0
  //   cns, otu        exposure of the previous dive, both optional
  //   helium          whether the series already involved a helium mix, optional
  //
  // The CNS decays with its 90 min half time, the OTU is carried over as it is
  // (see Gas.residualCns). Nothing here is guessed: an absent or unusable
  // tissue state, or a missing surface interval, is logged and refused rather
  // than replaced by clean tissues, which would plan less decompression than
  // the diver owes.
  applyPreviousDive(previousDive, context) {
    if (!previousDive) {
      this.model.reset()
      this.carriedOver = DivePlanner.NO_PREVIOUS_DIVE
      return this.carriedOver
    }

    const { tissues, surfaceInterval, cns = 0, otu = 0, helium = false } = previousDive

    // A Buhlmann instance is accepted alongside the serialized form: chaining
    // dives in memory must not pay a JSON round trip, and the round trip is
    // exactly what the persistence layer needs.
    const model = tissues instanceof Buhlmann
      ? tissues.clone()
      : Buhlmann.fromJSON(tissues, this.algorithm, this.environment, context)

    // The dive being planned owns the algorithm and the environment, whatever
    // the previous one was computed with. Tensions are pressures and carry over
    // unchanged; only their interpretation belongs to this dive.
    model.setAlgorithm(this.algorithm)
    model.setEnvironment(this.environment)

    // Measured on the state as it was handed in, before the surface interval:
    // a week at the surface legitimately brings every compartment back to
    // equilibrium, but a state that is already there when it claims to follow
    // a dive carries no dive at all. fromJSON cannot refuse it - a low tension
    // is legitimate per compartment, an O2 deco leaves the fast ones well under
    // equilibrium - so it is reported as a warning by residualStateWarning.
    const unloadedState = model.residualOverpressure() === 0

    model.applySurfaceInterval(surfaceInterval, context)

    let carriedOtu = otu
    if (!Number.isFinite(carriedOtu) || carriedOtu < 0) {
      console.error('%s: invalid OTU carried over (%o), expected a finite value >= 0, treating it as 0', context, otu)
      carriedOtu = 0
    }

    this.model = model
    this.carriedOver = {
      cns: Gas.residualCns(cns, surfaceInterval),
      otu: carriedOtu,
      surfaceInterval,
      repetitive: true,
      unloadedState,
      // Helium never leaves the series once it entered it: the DAN case is about
      // the diving that precedes the flight, and an air dive following a trimix
      // dive on the same day does not undo the trimix one.
      helium: helium === true
    }

    debugPlanner('%s: repetitive dive, %o min surface interval, CNS %o%% -> %o%%, OTU carried over %o',
      context, surfaceInterval, cns, this.carriedOver.cns.toFixed(1), carriedOtu)

    return this.carriedOver
  }

  // Ceiling the diver already owes when getting back in the water.
  //
  // After a short surface interval the tissues can still be above their
  // surface tolerance: the ceiling at 0 m is deeper than the surface, which
  // means the diver was past it while standing on the boat. The dive is still
  // planned - the model carries the load and the ascent pays it back - but
  // nothing else in the plan says the series started in the red, and a diver
  // reading a normal looking schedule would never guess it.
  //
  // The ceiling is read at the surface under GF High (firstStopDepth = 0, the
  // convention of the whole engine) and compared with the tolerance every
  // other ceiling comparison uses - and it is GF High of THIS dive, which is
  // the whole point. A dive planned with the same gradient factors as the
  // previous one can never trigger it: the previous ascent surfaced the diver
  // exactly at its own GF High and the surface interval only lowers the load.
  // What it catches is the diver who tightens the settings between two dives
  // (85/85 in the morning, 20/50 in the afternoon), a tissue state imported
  // from a computer or another tool, and a previous plan whose stop was
  // truncated at MAX_STOP_TIME. No false positive on a normal series, and the
  // three cases it does catch are exactly the ones nothing else reports.
  //
  // Returns the warning to carry in result.warnings, or null when the tissues
  // are clear. A first dive of the day never triggers it: its tissues sit at
  // the surface equilibrium.
  entryCeilingWarning(gfLow, gfHigh, context) {
    const ceiling = this.model.ceiling(gfLow, gfHigh, 0, 0)
    if (ceiling <= DivePlanner.CEILING_TOLERANCE_M) return null

    console.error('%s: the dive starts with a %om ceiling, the %o min surface interval does not clear the previous dive',
      context, ceiling.toFixed(1), this.carriedOver.surfaceInterval)

    return {
      code: PLAN_WARNING_CODES.ENTRY_CEILING,
      details: {
        context,
        ceiling: Math.round(ceiling * 10) / 10,
        interval: this.carriedOver.surfaceInterval
      }
    }
  }

  // Residual state handed in for a repetitive dive that carries no load at all:
  // not one compartment above the surface equilibrium of the site.
  //
  // Formally valid - every tension is a finite number in range - and physically
  // impossible for someone who just dived, so it is a corrupted state, a mapper
  // that wrote zeros, or a hand edited file. It matters because such a state is
  // LESS loaded than clean tissues and produces a shorter plan than the same
  // dive planned as a first dive: 40 m / 30 min on air drops from 59 min of TTS
  // to 34 min. Refusing it in fromJSON is not an option, a single low tension is
  // legitimate (an O2 decompression leaves the fast compartments well under
  // equilibrium), so the whole state is judged here, once, and reported.
  residualStateWarning(context) {
    if (!this.carriedOver.unloadedState) return null

    console.error('%s: the residual tissue state carries no load at all (no compartment above the surface equilibrium of the site), this plan is shorter than the same dive planned on clean tissues',
      context)

    return {
      code: PLAN_WARNING_CODES.RESIDUAL_STATE_UNLOADED,
      details: { context, surfaceInterval: this.carriedOver.surfaceInterval }
    }
  }

  // Series a computed plan belongs to, read from the plan itself.
  //
  // fillExposureTotals writes repetitive, cnsAtEntry, otuAtEntry and
  // surfaceIntervalBefore on every plan this engine produces, so a plan carries
  // its own series wherever it travels: a plan reloaded from storage, exported,
  // or simply handed to another planner instance must not answer 12 h where its
  // author answered 18 h.
  //
  // A plan carrying none was produced by an older version or another tool. The
  // planner state is then the only thing left to fall back on, and the fallback
  // is logged: it decides between the two DAN delays.
  static seriesOfPlan(plan, fallback, context) {
    if (!plan || typeof plan.repetitive !== 'boolean') {
      console.error('%s: the plan carries no series information (repetitive=%o), falling back on the planner state (repetitive=%o)',
        context, plan ? plan.repetitive : plan, fallback.repetitive)
      return fallback
    }

    return {
      // Rounded values, as the plan reports them: half a CNS point against a
      // series read consistently by every caller is the right trade
      cns: Number.isFinite(plan.cnsAtEntry) ? plan.cnsAtEntry : 0,
      otu: Number.isFinite(plan.otuAtEntry) ? plan.otuAtEntry : 0,
      surfaceInterval: plan.surfaceIntervalBefore ?? null,
      repetitive: plan.repetitive,
      // Helium of the whole series, not of the dive alone: a bailout replaying a
      // trimix diluent dive on air is still part of a dive involving helium
      helium: plan.heliumSeries === true
    }
  }

  // Round the oxygen exposure of a finished plan and report the totals of the
  // series alongside it.
  //
  // result.cns and result.otu keep their meaning - the exposure of this dive
  // alone - so every existing view, export and reference scenario reads the
  // same number as before. The series totals are new fields, and they are
  // rounded once from the unrounded values rather than from each other.
  //
  // `extra` is exposure already accumulated during this same dive before the
  // segment being planned: planBailout uses it for the CCR part the diver
  // breathed before losing the loop.
  fillExposureTotals(result, extra = { cns: 0, otu: 0 }) {
    const carried = this.carriedOver
    const cnsAtEntry = carried.cns + extra.cns
    const otuAtEntry = carried.otu + extra.otu
    result.cnsAtEntry = Math.round(cnsAtEntry)
    result.otuAtEntry = Math.round(otuAtEntry)
    result.cnsTotal = Math.round(cnsAtEntry + result.cns)
    result.otuTotal = Math.round(otuAtEntry + result.otu)
    result.cns = Math.round(result.cns)
    result.otu = Math.round(result.otu)
    result.repetitive = carried.repetitive
    result.surfaceIntervalBefore = carried.surfaceInterval
  }

  // Plan an open circuit dive
  planOC({ segments, gases, gfLow = 0.30, gfHigh = 0.85, rmvBottom = 20, rmvDeco = 15, previousDive = null }) {
    this.validateSegments(segments, 'planOC')
    this.validateGradientFactors(gfLow, gfHigh, 'planOC')
    this.validateGases(gases, 'planOC')

    debugPlanner('planOC: %d segments, %d gases, GF %o/%o', segments.length, gases.length, Math.round(gfLow * 100), Math.round(gfHigh * 100))
    this.applyPreviousDive(previousDive, 'planOC')

    const initialWarnings = []

    // Checked here and not inside applyPreviousDive: the gradient factors of
    // this dive are what decides whether the residual load is still a ceiling
    const entryCeiling = this.entryCeilingWarning(gfLow, gfHigh, 'planOC')
    if (entryCeiling) initialWarnings.push(entryCeiling)

    const unloadedState = this.residualStateWarning('planOC')
    if (unloadedState) initialWarnings.push(unloadedState)

    // An empty gas list is a legitimate user state (every gas disabled):
    // the plan falls back to air instead of failing, as it always did. But air
    // is only breathable down to about 66 m at ppO2 1.4, so a deep profile is
    // then planned on a mix the diver never carried: log it and carry the
    // warning in the result so the UI can tell the diver.
    if (!gases.length) {
      const deepest = segments.reduce((max, s) => Math.max(max, s.depth), 0)
      console.error('planOC: no gas provided for a %om dive, falling back to air (this plan is not the configured gas plan)', deepest)
      initialWarnings.push({
        code: PLAN_WARNING_CODES.DEFAULT_GAS_FALLBACK,
        details: { context: 'planOC', depth: deepest, gas: 'Air' }
      })
      gases = [{ name: 'Air', fO2: 0.21, fHe: 0, fN2: 0.79, enabled: true }]
    }

    // Recompute fN2 and generate the missing display names: store gases are
    // plain literals, an unnamed one used to travel through the plan as
    // `gas: undefined` and defeat the name based gas switch entirely.
    gases = gases.map(g => Gas.normalizeMix(g))

    const result = this.ocPass(segments, gases, gfLow, gfHigh, null, initialWarnings)

    this.fillExposureTotals(result)

    // Calculate gas consumption (use rmvBottom for bottom segments, rmvDeco for deco)
    result.gasConsumption = this.calculateGasConsumptionOC(result.segments, gases, rmvBottom, rmvDeco)
    result.warnings.push(...this.gasInsufficientWarnings(result.gasConsumption, 'planOC'))

    this.fillSurfaceIntervalInfo(result)

    return result
  }

  // One full open circuit pass at a fixed gradient factor reference depth.
  //
  // Walks the requested levels in order, then ascends to the surface. A level
  // shallower than the previous one is not reachable for free: when the
  // ceiling is deeper than that level, the pass owes the stops that clear it
  // and holds them through decoPass bounded at the level (floorDepth), exactly
  // as it would before surfacing. Without that, the plan used to ascend
  // straight through its own ceiling and lose every deep stop of the dive.
  //
  // gfReferenceDepth is the reference the whole pass interpolates with, or
  // null until the dive owes its first stop - in which case the first ascent
  // that decompresses derives it and the rest of the dive keeps it.
  ocPass(segments, gases, gfLow, gfHigh, gfReferenceDepth, initialWarnings = []) {
    const result = {
      segments: [],
      stops: [],
      warnings: [...initialWarnings],
      tts: 0,
      cns: 0,
      otu: 0,
      runtime: 0,
      maxDepth: 0
    }

    // GF Low reference: the deepest GF Low ceiling met so far, kept as a
    // running maximum across the levels and the passes (see gfReferenceFloor)
    let gfReference = this.usableReference(gfReferenceDepth, 'ocPass')

    // Process each segment (multi-level support)
    let currentDepth = 0
    // Mix in the diver's mouth, null before the first segment. A level is
    // reached on the mix of the previous level and the change of mix, when
    // there is one, is written as its own row: before a descent (at the
    // shallower level) and after an ascent (at the level joined, once the
    // ceiling and the MOD allow it). Descending on the deeper gas from the
    // shallower level, or ascending 40 m on the EAN50 of the level being
    // joined, described a switch no table showed and no diver would make.
    let currentGas = null
    for (const segment of segments) {
      const targetDepth = segment.depth
      const bottomTime = segment.time
      result.maxDepth = Math.max(result.maxDepth, targetDepth)

      // Get gas for this segment
      const segmentGas = segment.gasIndex !== undefined
        ? gases[segment.gasIndex]
        : Gas.bestGas(gases, targetDepth, this.ppO2Max, this.environment)

      if (!segmentGas) {
        console.error('planOC: segment at %om references gasIndex=%o but only %d gases are available',
          targetDepth, segment.gasIndex, gases.length)
        throw new PlanError(PLAN_ERROR_CODES.INVALID_GAS, {
          context: 'planOC', depth: targetDepth, gasIndex: segment.gasIndex,
          available: gases.length, reason: 'gas_index_out_of_range'
        })
      }

      // Holds for a gas the diver picked himself as well as for one bestGas
      // fell back on: a gasIndex pointing at a mix too rich for that level is
      // the same danger, and the segment is planned on it either way.
      const gasAboveMOD = this.gasAboveMODWarning(segmentGas, targetDepth, this.ppO2Max, 'planOC')
      if (gasAboveMOD) result.warnings.push(gasAboveMOD)

      // Descent or ascent to target depth
      if (targetDepth > currentDepth) {
        // The deeper mix is put in the mouth before going down, at the level
        // the diver is leaving: the first segment starts on its own gas.
        // (the gas in the mouth becomes segmentGas, written after the bottom)
        if (currentGas) {
          this.switchToGas(currentDepth, segmentGas, currentGas, result, gfLow, gfHigh, gfReference)
        }
        const transitionTime = (targetDepth - currentDepth) / this.descentRate
        // Use Schreiner equation for variable pressure during descent
        this.loadTissuesAscDecGuarded(this.model, currentDepth, targetDepth, transitionTime, segmentGas, 'planOC descent')
        result.runtime += transitionTime
        const avgDepth = (currentDepth + targetDepth) / 2
        const avgPpO2 = Gas.ppO2(segmentGas.fO2, avgDepth, this.environment)
        result.cns += Gas.calcCNS(avgPpO2, transitionTime)
        result.otu += Gas.calcOTU(avgPpO2, transitionTime)

        // Get tissue info at end of descent
        gfReference = this.noteGfReference(gfReference, targetDepth, gfLow, gfHigh)
        const tissueInfo = this.model.getLeadingTissueInfo(gfLow, gfHigh, targetDepth, gfReference)
        const compartments = this.model.getCompartmentState(targetDepth)
        const ceiling = this.model.ceiling(gfLow, gfHigh, targetDepth, gfReference)

        result.segments.push({
          type: 'descent',
          startDepth: currentDepth,
          endDepth: targetDepth,
          time: transitionTime,
          runtime: result.runtime,
          gas: segmentGas.name,
          fO2: segmentGas.fO2,
          fHe: segmentGas.fHe || 0,
          ppO2: avgPpO2,
          end: Gas.calcEND(targetDepth, segmentGas.fO2, segmentGas.fHe || 0, this.environment),
          tissueInfo,
          compartments,
          ceiling
        })
      } else if (targetDepth < currentDepth) {
        // Up to a shallower level: the same walk as the final ascent, bounded
        // at that level. It holds whatever stops the ceiling owes before the
        // level is reachable, and takes the deco mixes on the way at their MOD.
        const transition = this.decoPass(currentDepth, result.maxDepth, gases, gfLow, gfHigh,
          result.runtime, gfReference, targetDepth, currentGas)
        result.segments.push(...transition.segments)
        result.stops.push(...transition.stops)
        result.warnings.push(...transition.warnings)
        result.runtime = transition.runtime
        result.cns += transition.cns
        result.otu += transition.otu
        gfReference = Math.max(gfReference, transition.gfReferenceDepth)
        currentDepth = transition.endDepth
        // The pass may have changed mix on the way up (deco gas at its MOD)
        currentGas = this.gasBreathedAtEnd(transition.segments, gases, currentGas)

        // Remaining ascent to the level, on the mix in the mouth. Nothing is
        // left to do in the normal case (decoPass ends at the level), but a
        // decompression interrupted at MAX_RUNTIME stops deeper, and the diver
        // still has to be brought to the level he asked for.
        if (targetDepth < currentDepth) {
          const ascentGas = currentGas || segmentGas
          const transitionTime = (currentDepth - targetDepth) / this.getAscentRate(targetDepth)
          // Use Schreiner equation for variable pressure during ascent
          this.loadTissuesAscDecGuarded(this.model, currentDepth, targetDepth, transitionTime, ascentGas, 'planOC ascent')
          result.runtime += transitionTime
          const avgPpO2 = Gas.ppO2(ascentGas.fO2, (currentDepth + targetDepth) / 2, this.environment)
          result.cns += Gas.calcCNS(avgPpO2, transitionTime)
          result.otu += Gas.calcOTU(avgPpO2, transitionTime)
          const breach = this.ceilingBreachAtArrival(currentDepth, targetDepth, gfLow, gfHigh, gfReference, 'ocPass')

          // Get tissue info at end of ascent
          const tissueInfo = this.model.getLeadingTissueInfo(gfLow, gfHigh, targetDepth, gfReference)
          const compartments = this.model.getCompartmentState(targetDepth)
          const ceiling = this.model.ceiling(gfLow, gfHigh, targetDepth, gfReference)

          const ascentSegment = {
            type: 'ascent',
            startDepth: currentDepth,
            endDepth: targetDepth,
            time: transitionTime,
            runtime: result.runtime,
            gas: ascentGas.name,
            fO2: ascentGas.fO2,
            fHe: ascentGas.fHe || 0,
            ppO2: avgPpO2,
            end: Gas.calcEND(targetDepth, ascentGas.fO2, ascentGas.fHe || 0, this.environment),
            tissueInfo,
            compartments,
            ceiling
          }
          if (breach) ascentSegment.ceilingBreach = breach
          result.segments.push(ascentSegment)
        }

        // The level is reached: its own mix goes in the mouth, as a row
        if (currentGas) {
          this.switchToGas(targetDepth, segmentGas, currentGas, result, gfLow, gfHigh, gfReference)
        }
      } else if (currentGas && currentGas.name !== segmentGas.name) {
        // Same depth, another mix: a change written as a row, not as a bottom
        // segment that silently names another gas than the one before it
        this.switchToGas(targetDepth, segmentGas, currentGas, result, gfLow, gfHigh, gfReference)
      }

      currentDepth = targetDepth

      // Bottom time at depth
      this.loadTissuesGuarded(this.model, targetDepth, bottomTime, segmentGas, 'planOC bottom')
      result.runtime += bottomTime
      const bottomPpO2 = Gas.ppO2(segmentGas.fO2, targetDepth, this.environment)
      result.cns += Gas.calcCNS(bottomPpO2, bottomTime)
      result.otu += Gas.calcOTU(bottomPpO2, bottomTime)

      // Get tissue info at end of bottom time
      gfReference = this.noteGfReference(gfReference, targetDepth, gfLow, gfHigh)
      const tissueInfo = this.model.getLeadingTissueInfo(gfLow, gfHigh, targetDepth, gfReference)
      const compartments = this.model.getCompartmentState(targetDepth)
      const ceiling = this.model.ceiling(gfLow, gfHigh, targetDepth, gfReference)

      result.segments.push({
        type: 'bottom',
        depth: targetDepth,
        time: bottomTime,
        runtime: result.runtime,
        gas: segmentGas.name,
        fO2: segmentGas.fO2,
        fHe: segmentGas.fHe || 0,
        ppO2: bottomPpO2,
        end: Gas.calcEND(targetDepth, segmentGas.fO2, segmentGas.fHe || 0, this.environment),
        tissueInfo,
        compartments,
        ceiling
      })

      debugPlannerSegments('bottom: %dm for %dmin with %s, ceiling=%om, runtime=%o',
        targetDepth, bottomTime, segmentGas.name, ceiling.toFixed(1), result.runtime.toFixed(1))
      currentGas = segmentGas
    }

    // Calculate decompression from current depth, starting on the mix the
    // diver leaves the last level with: the ascent writes its own switch to
    // the first deco gas instead of silently starting on it.
    const runtimeBeforeDeco = result.runtime
    const decoResult = this.decoPass(currentDepth, result.maxDepth, gases, gfLow, gfHigh, result.runtime, gfReference, 0, currentGas)
    result.segments.push(...decoResult.segments)
    result.stops.push(...decoResult.stops)
    result.runtime = decoResult.runtime
    result.cns += decoResult.cns
    result.otu += decoResult.otu
    result.warnings.push(...decoResult.warnings)
    result.tts = Math.ceil(decoResult.runtime - runtimeBeforeDeco)
    // Depth the gradient factor equals GF Low at: the deepest ceiling of the dive
    result.gfReferenceDepth = Math.max(gfReference, decoResult.gfReferenceDepth)

    return result
  }

  // Calculate gas consumption for OC dive (different RMV for bottom vs deco)
  calculateGasConsumptionOC(segments, gases, rmvBottom, rmvDeco) {
    const consumption = {}

    // Initialize consumption for each gas. firstUse is the rank of the segment
    // that first puts the mix in the diver's mouth, so the requirement table
    // can be read in the order the plan is dived; Infinity until then, which
    // sorts the cylinders the plan never opens after the ones it breathes.
    for (const gas of gases) {
      consumption[gas.name] = {
        gas: gas,
        liters: 0,
        maxDepth: 0,
        firstUse: Infinity
      }
    }

    // Capacity and water volume of the cylinders holding each mix, for the
    // pressure left printed next to the gas of every row. Summed over the
    // cylinders of a mix, as the verdict is: reading it off the first cylinder
    // of the name, as this used to, printed a pressure below zero on the
    // second twin cylinder of a mix the plan was breathing comfortably.
    const mixKey = gas => GasBudget.mixKey(gas)
    const capacityOf = new Map()
    const volumeOf = new Map()
    for (const gas of gases) {
      const key = mixKey(gas)
      const volume = gas.tankVolume || DivePlanner.DEFAULT_TANK_VOLUME
      const pressure = gas.tankPressure || DivePlanner.DEFAULT_TANK_PRESSURE
      capacityOf.set(key, (capacityOf.get(key) || 0) + volume * pressure)
      volumeOf.set(key, (volumeOf.get(key) || 0) + volume)
    }
    const spentOf = new Map()

    // Calculate consumption per segment
    for (const [index, segment] of segments.entries()) {
      const gasName = segment.gas
      if (!consumption[gasName]) continue

      // Use rmvBottom for descent and bottom, rmvDeco for ascent and stops
      const rmv = (segment.type === 'descent' || segment.type === 'bottom') ? rmvBottom : rmvDeco
      const liters = DivePlanner.litersFor(segment, rmv, this.environment)
      if (!(liters > 0)) continue

      consumption[gasName].liters += liters
      consumption[gasName].maxDepth = Math.max(consumption[gasName].maxDepth, DivePlanner.segmentDeepestPoint(segment))
      consumption[gasName].firstUse = Math.min(consumption[gasName].firstUse, index)

      const key = mixKey(consumption[gasName].gas)
      spentOf.set(key, (spentOf.get(key) || 0) + liters)
      segment.gasLiter = spentOf.get(key)
      segment.gasPressureLeft = Math.ceil((capacityOf.get(key) - spentOf.get(key)) / volumeOf.get(key))
    }

    return this.summarizeGasConsumption(consumption, gases)
  }

  // The gases still worth offering to the ascent: those whose cylinder is not
  // empty yet. Sorting on the oxygen fraction alone made the richest mix take
  // the whole ascent however small its cylinder, and left a full one untouched
  // beside it.
  //
  // When every usable cylinder is dry the complete list is handed back - the
  // diver still has to surface, and a plan is more use than none - but the
  // fact that it now prescribes gas nobody carries is reported once, loudly.
  availableGases(gases, depth, result = null) {
    if (!this.gasBudget?.tracked) return gases

    // Emptiness is judged among the mixes that reach this depth, never across
    // the whole list. Dropping an empty trimix from a 70 m list would leave
    // bestGas to fall back on the deco bottles and prescribe EAN75 at 70 m:
    // running out of bottom gas must not turn into an oxygen hit.
    const reachesHere = g => Gas.reachesDepth(g, depth, this.ppO2Deco, this.environment)
    const usableHere = gases.filter(reachesHere)
    const withGasHere = usableHere.filter(g => this.gasBudget.hasGasLeft(g))
    if (withGasHere.length) {
      // Last net under a forced switch. The two passes announce the switch
      // themselves, with the exact depth the cylinder dies at; but a cylinder
      // can also simply stop being offered here, with nothing having asked to
      // split a segment, and that switch would otherwise reach the diver as a
      // plain gas change line at an unplanned depth.
      if (result && Array.isArray(result.warnings)) {
        for (const empty of usableHere.filter(g => !this.gasBudget.hasGasLeft(g))) {
          const dry = this.gasSwitchOnEmptyWarning(empty, depth, 'availableGases')
          if (dry) result.warnings.push(dry)
        }
      }
      return gases.filter(g => this.gasBudget.hasGasLeft(g))
    }

    if (!this.gasBudget.exhausted) {
      this.gasBudget.exhausted = true
      console.error('availableGases: every cylinder is empty at %om, the plan keeps breathing gas the diver no longer carries',
        Math.round(depth))
      if (result && Array.isArray(result.warnings)) {
        result.warnings.push({
          code: PLAN_WARNING_CODES.GAS_EXHAUSTED,
          details: { depth: Math.round(depth) }
        })
      }
    }
    return gases
  }

  // Shallowest depth the current cylinder can pay the ascent up to, between
  // toDepth (the whole leg) and fromDepth (not a metre of it).
  //
  // The cost of a leg is not linear in depth - it is rate, time and mean
  // ambient pressure multiplied together - so the crossing point is found by
  // bisection rather than solved. Forty halvings put it well inside a
  // centimetre, which is far below the tolerance anything downstream uses.
  depthWithinGasBudget(fromDepth, toDepth, gas, inDecoZone) {
    const left = this.gasBudget.remaining(gas)
    if (!Number.isFinite(left)) return toDepth

    const costUpTo = depth => this.gasBudget.costOf({
      type: 'ascent',
      startDepth: fromDepth,
      endDepth: depth,
      gas: gas.name,
      fO2: gas.fO2,
      fHe: gas.fHe || 0,
      time: (fromDepth - depth) / this.getAscentRate(depth, inDecoZone)
    })

    if (costUpTo(toDepth) <= left) return toDepth

    let deepest = toDepth
    let shallowest = fromDepth
    for (let i = 0; i < 40; i++) {
      const mid = (deepest + shallowest) / 2
      if (costUpTo(mid) > left) deepest = mid
      else shallowest = mid
    }
    return shallowest
  }

  // Is there another mix, still holding gas, that could be breathed here in
  // place of `gas`? Splitting a segment on an emptying cylinder only makes
  // sense when something can take over at that very depth.
  hasReliefFor(gas, depth, gases) {
    if (!this.gasBudget?.tracked) return false
    const currentKey = GasBudget.mixKey(gas)
    return gases.some(g =>
      GasBudget.mixKey(g) !== currentKey &&
      this.gasBudget.hasGasLeft(g) &&
      Gas.reachesDepth(g, depth, this.ppO2Deco, this.environment)
    )
  }

  // Deepest point a segment reaches, the depth a replacement cylinder has to
  // cover to stand in for the one breathed there.
  static segmentDeepestPoint(segment) {
    const candidates = [segment.depth, segment.startDepth, segment.endDepth]
      .filter(d => Number.isFinite(d))
    return candidates.length ? Math.max(...candidates) : 0
  }

  // Turn the per gas totals into the requirement lines the screens and the
  // exporters print, and judge whether the diver carries enough.
  //
  // Two rules decide the verdict, and they are not symmetrical:
  //
  //  - Cylinders holding the SAME mix add up. Two 7 L / 200 bar of one trimix
  //    are 2800 L whichever regulator is in the mouth, and the plan is the same
  //    either way. Indexing the requirement by gas name collapsed them into a
  //    single 1400 L line and overstated the shortfall by a whole cylinder.
  //
  //  - Cylinders holding a DIFFERENT mix never join the verdict, they are
  //    reported as backup. Breathing the other mix is another dive: on the
  //    70 m CCR plan this came from, falling back from TX 17/37 to the leaner
  //    TX14/48 stretches the ascent from 103 to 125 min and pushes the oxygen
  //    requirement from 1211 L to 1440 L, which no longer fits its own
  //    cylinder. Folding 3600 L of 14/48 into the 17/37 line would have
  //    declared sound a plan whose deco had not been computed.
  summarizeGasConsumption(consumption, gases) {
    const mixKey = gas => `${Number(gas.fO2 || 0).toFixed(4)}/${Number(gas.fHe || 0).toFixed(4)}`
    const list = Array.isArray(gases) ? gases : []

    // Cylinders carried, grouped by the mix they hold.
    const carried = new Map()
    for (const gas of list) {
      const key = mixKey(gas)
      if (!carried.has(key)) carried.set(key, { gas, tanks: [] })
      carried.get(key).tanks.push({
        volume: gas.tankVolume || DivePlanner.DEFAULT_TANK_VOLUME,
        pressure: gas.tankPressure || DivePlanner.DEFAULT_TANK_PRESSURE,
        // Whether the diver described this cylinder, or the defaults stand in
        declared: Number.isFinite(gas.tankVolume) && gas.tankVolume > 0
          && Number.isFinite(gas.tankPressure) && gas.tankPressure > 0
      })
    }

    // What the plan asked of each mix, how deep it asked it, and when it first
    // put it in the diver's mouth.
    const used = new Map()
    for (const entry of Object.values(consumption)) {
      const key = mixKey(entry.gas)
      const current = used.get(key) || { liters: 0, maxDepth: 0, firstUse: Infinity }
      used.set(key, {
        liters: current.liters + entry.liters,
        maxDepth: Math.max(current.maxDepth, entry.maxDepth || 0),
        firstUse: Math.min(current.firstUse, entry.firstUse ?? Infinity)
      })
    }

    const rows = []
    for (const [key, { gas, tanks }] of carried) {
      const { liters = 0, maxDepth = 0, firstUse = Infinity } = used.get(key) || {}
      const tankVolume = tanks.reduce((sum, t) => sum + t.volume, 0)
      const tankCapacity = tanks.reduce((sum, t) => sum + t.volume * t.pressure, 0)
      const pressures = new Set(tanks.map(t => t.pressure))
      const litersNeeded = Math.ceil(liters)
      const barsNeeded = Math.ceil(liters / tankVolume)

      // Other mixes that could take over where this one is breathed. Judged at
      // the working limit, the stricter of the two the diver set: a cylinder
      // offered as backup has to be breathable at the depth it stands in for.
      //
      // And it has to still hold gas at the end of the plan. The full capacity
      // of the other cylinders was offered whatever this very ascent had
      // already burned from them: on the 70 m plan this comes from, the line
      // short of 195 L of TX14/48 offered 'backup: TX 17/37' about a cylinder
      // the same ascent had drained to its last litre at 42 m. What is
      // reported is what the plan leaves in them (`free`), and a mix with
      // nothing left is not offered at all.
      const backup = []
      if (liters > 0) {
        for (const [otherKey, other] of carried) {
          if (otherKey === key) continue
          if (!Gas.reachesDepth(other.gas, maxDepth, this.ppO2Max, this.environment)) continue
          const otherCapacity = other.tanks.reduce((sum, t) => sum + t.volume * t.pressure, 0)
          const free = Math.floor(otherCapacity - (used.get(otherKey)?.liters || 0))
          if (free <= 0) continue
          backup.push({ name: other.gas.name, tankCapacity: otherCapacity, free })
        }
      }

      const insufficient = litersNeeded > tankCapacity

      rows.push({
        name: gas.name,
        fO2: gas.fO2,
        liters: litersNeeded,
        tankVolume,
        tankPressure: pressures.size === 1 ? tanks[0].pressure : Math.round(tankCapacity / tankVolume),
        tanks,
        tankCapacity,
        barsNeeded,
        maxDepth,
        backup,
        insufficient,
        // Enough, but with no reserve worth the name. A cylinder the plan
        // empties to the litre is not sound, it is exactly spent: a minute of
        // stop added by a colder day, a deeper reading or a faster breath and
        // it is short.
        tight: !insufficient && liters > 0
          && tankCapacity - litersNeeded < tankCapacity * DivePlanner.GAS_RESERVE_FRACTION,
        // True when every cylinder of the line was described by the diver.
        // A verdict against DEFAULT_TANK_* is a table convenience, not a fact
        // about gas the diver carries, and it is not turned into a warning.
        declared: tanks.every(t => t.declared),
        firstUse
      })
    }

    // In the order the diver breathes them, then the cylinders the plan never
    // opens. Sorting on the oxygen fraction listed the bottom trimix of a
    // bailout after the leaner one it switches to later, and the requirement
    // table no longer read in the same order as the plan right above it.
    // Ties and cylinders never breathed keep the oxygen order, which is the one
    // an ascent follows anyway.
    // Compared, not subtracted: two cylinders the plan never opens both carry
    // Infinity, and Infinity - Infinity is NaN, which makes a comparator lie.
    return rows.sort((a, b) => {
      if (a.firstUse !== b.firstUse) return a.firstUse < b.firstUse ? -1 : 1
      return a.fO2 - b.fO2
    })
  }

  // Plan a CCR dive
  // `bailoutGases` may be passed along with the request and is ignored here:
  // the bailout is planned by planBailout, from the plan this returns.
  planCCR({ segments, ccr, diluent, flushes = null, flushWarnings = [], gfLow = 0.30, gfHigh = 0.85, previousDive = null }) {
    this.validateSegments(segments, 'planCCR')
    this.validateGradientFactors(gfLow, gfHigh, 'planCCR')
    this.validateCCRConfig(ccr, diluent, segments)
    const plannedFlushes = this.normalizeFlushes(flushes, segments, 'planCCR')

    // Same normalization as planOC does for its gases: the diluent literal
    // built by the views or restored from storage often carries no fN2, and
    // fN2 is exactly what the loop calculation feeds to the tissues.
    diluent = CCR.normalizeDiluent(diluent, 'planCCR')

    debugPlanner('planCCR: %d segments, diluent=%s, GF %o/%o', segments.length, diluent.name, Math.round(gfLow * 100), Math.round(gfHigh * 100))
    this.applyPreviousDive(previousDive, 'planCCR')

    // Same entry ceiling check as planOC, for the same reason: a rebreather
    // does not make a short surface interval any shorter
    // Warnings raised before the pass even starts: what the caller found while
    // turning the settings into flushes (DayPlanner.resolveFlushes), then what
    // the tissues say at the water's edge. A flush the settings asked for and
    // that could not be applied belongs in the plan, not only in the console.
    const initialWarnings = Array.isArray(flushWarnings) ? [...flushWarnings] : []
    if (!Array.isArray(flushWarnings)) {
      console.error('planCCR: flushWarnings is not a list (%o), dropped', flushWarnings)
    }
    const entryCeiling = this.entryCeilingWarning(gfLow, gfHigh, 'planCCR')
    if (entryCeiling) initialWarnings.push(entryCeiling)

    const unloadedState = this.residualStateWarning('planCCR')
    if (unloadedState) initialWarnings.push(unloadedState)

    // Update CCR diluent (default)
    ccr.diluent = diluent
    // The planner owns the dive environment: push it into the loop calculations
    // so the equivalent gas, the loop mix and the model share the same altitude
    // and water type (same mutation pattern as ccr.diluent just above).
    ccr.setEnvironment(this.environment)

    const result = this.ccrPass(segments, ccr, diluent, gfLow, gfHigh, null, initialWarnings, plannedFlushes)

    this.fillExposureTotals(result)

    this.fillSurfaceIntervalInfo(result)

    return result
  }

  // One full CCR pass at a fixed gradient factor reference depth.
  // Same structure and same contract as ocPass, on the CCR equivalent gas.
  ccrPass(segments, ccr, diluent, gfLow, gfHigh, gfReferenceDepth, initialWarnings = [], flushes = []) {
    const result = {
      segments: [],
      stops: [],
      warnings: [...initialWarnings],
      tts: 0,
      cns: 0,
      otu: 0,
      runtime: 0,
      maxDepth: 0
    }

    // GF Low reference, a running maximum as in ocPass
    let gfReference = this.usableReference(gfReferenceDepth, 'ccrPass')

    // Track current diluent for detecting switches
    let currentDiluent = diluent
    ccr.diluent = diluent

    // Worst moment to lose the loop, kept for planBailout: the end of the
    // deepest bottom segment, i.e. the most demanding open circuit ascent of
    // the dive, both in gas and in decompression. On a single level dive that
    // is the end of the bottom time, as it always was; on a multilevel profile
    // it is no longer the last level, and pairing the tissue state of a 5 m
    // level with an ascent started at 50 m described a dive nobody made.
    let bailoutPoint = null

    // Process each segment
    let currentDepth = 0
    for (const segment of segments) {
      const targetDepth = segment.depth
      const bottomTime = segment.time
      result.maxDepth = Math.max(result.maxDepth, targetDepth)

      // Get segment's diluent (if specified) or use current. A per segment
      // diluent comes from the same literals as the default one, so it goes
      // through the same normalization before reaching the loop calculation.
      const segmentDiluent = segment.diluent
        ? CCR.normalizeDiluent(segment.diluent, 'planCCR segment')
        : currentDiluent

      // Check for diluent change
      if (segmentDiluent.name !== currentDiluent.name) {
        // Add diluent change segment
        result.segments.push({
          type: 'diluentChange',
          depth: currentDepth,
          time: 0,
          runtime: result.runtime,
          gas: segmentDiluent.name,
          previousGas: currentDiluent.name
        })
        currentDiluent = segmentDiluent
        ccr.diluent = currentDiluent
      }

      // Phase of the transition, for the dynamic setpoint: DESCENT going down,
      // ASCENT going up, the level itself is held at BOTTOM. An ascent between
      // two levels used to be computed at the bottom setpoint while the
      // decompression ascent took the ascent one, so the same 30 -> 15 m leg
      // read 1.3 bar in one place and 1.0 bar in the other.
      const isDescending = targetDepth > currentDepth
      const phase = isDescending ? CCR.PHASES.DESCENT : CCR.PHASES.ASCENT

      // Up to a shallower level: the same walk as the final ascent, bounded at
      // that level, holding whatever stops the ceiling owes on the way
      if (targetDepth < currentDepth) {
        const transition = this.decoPassCCR(currentDepth, result.maxDepth, ccr, currentDiluent,
          gfLow, gfHigh, result.runtime, gfReference, targetDepth)
        result.segments.push(...transition.segments)
        result.stops.push(...transition.stops)
        result.warnings.push(...transition.warnings)
        result.runtime = transition.runtime
        result.cns += transition.cns
        result.otu += transition.otu
        gfReference = Math.max(gfReference, transition.gfReferenceDepth)
        currentDepth = transition.endDepth
      }

      // Descent, or the remaining ascent of a pass MAX_RUNTIME interrupted
      if (targetDepth !== currentDepth) {
        const avgDepth = (currentDepth + targetDepth) / 2
        const equivGas = ccr.getEquivalentGas(avgDepth, phase)
        const transitionTime = isDescending
          ? (targetDepth - currentDepth) / this.descentRate
          : (currentDepth - targetDepth) / this.getAscentRate(targetDepth)

        // Integrated between the two ends of the leg, as the open circuit
        // transitions are; equivGas at the mean depth only serves the ppO2
        // and the exposure, which are the setpoint anyway.
        this.loadCCRLeg(this.model, ccr, currentDepth, targetDepth, transitionTime, phase, 'planCCR transition')
        result.runtime += transitionTime
        result.cns += Gas.calcCNS(equivGas.ppO2, transitionTime)
        result.otu += Gas.calcOTU(equivGas.ppO2, transitionTime)
        gfReference = this.noteGfReference(gfReference, targetDepth, gfLow, gfHigh)
        const breach = targetDepth < currentDepth
          ? this.ceilingBreachAtArrival(currentDepth, targetDepth, gfLow, gfHigh, gfReference, 'ccrPass')
          : null

        // Get tissue info at end of transition
        const tissueInfo = this.model.getLeadingTissueInfo(gfLow, gfHigh, targetDepth, gfReference)
        const compartments = this.model.getCompartmentState(targetDepth)
        const ceiling = this.model.ceiling(gfLow, gfHigh, targetDepth, gfReference)

        // Loop gas mix at target depth (for density / narcosis displays)
        const loopMix = ccr.getLoopGasMix(targetDepth, phase)

        const transitionSegment = {
          type: isDescending ? 'descent' : 'ascent',
          startDepth: currentDepth,
          endDepth: targetDepth,
          time: transitionTime,
          runtime: result.runtime,
          gas: currentDiluent.name,
          fO2: loopMix.fO2,
          fHe: loopMix.fHe,
          ppO2: equivGas.ppO2,
          setpoint: ccr.heldSetpoint(targetDepth, phase),
          end: ccr.getEND(targetDepth, phase),
          tissueInfo,
          compartments,
          ceiling
        }
        if (breach) transitionSegment.ceilingBreach = breach
        result.segments.push(transitionSegment)
      }

      currentDepth = targetDepth

      // Bottom time
      const bottomEquivGas = ccr.getEquivalentGas(targetDepth, CCR.PHASES.BOTTOM)
      this.loadTissuesGuarded(this.model, targetDepth, bottomTime, bottomEquivGas, 'planCCR bottom')
      result.runtime += bottomTime
      result.cns += Gas.calcCNS(bottomEquivGas.ppO2, bottomTime)
      result.otu += Gas.calcOTU(bottomEquivGas.ppO2, bottomTime)

      // Get tissue info at end of bottom time
      gfReference = this.noteGfReference(gfReference, targetDepth, gfLow, gfHigh)
      const tissueInfo = this.model.getLeadingTissueInfo(gfLow, gfHigh, targetDepth, gfReference)
      const compartments = this.model.getCompartmentState(targetDepth)
      const ceiling = this.model.ceiling(gfLow, gfHigh, targetDepth, gfReference)

      const bottomLoopMix = ccr.getLoopGasMix(targetDepth, CCR.PHASES.BOTTOM)

      result.segments.push({
        type: 'bottom',
        depth: targetDepth,
        time: bottomTime,
        runtime: result.runtime,
        gas: currentDiluent.name,
        fO2: bottomLoopMix.fO2,
        fHe: bottomLoopMix.fHe,
        ppO2: bottomEquivGas.ppO2,
        setpoint: ccr.heldSetpoint(targetDepth, CCR.PHASES.BOTTOM),
        end: ccr.getEND(targetDepth, CCR.PHASES.BOTTOM),
        tissueInfo,
        compartments,
        ceiling
      })

      // `>=` and not `>`: at equal depth the later level is the loaded one.
      // cns and otu are the exposure already accumulated when the loop is
      // lost: the bailout ascent adds to it, it does not start from zero.
      if (!bailoutPoint || targetDepth >= bailoutPoint.depth) {
        bailoutPoint = {
          depth: targetDepth,
          runtime: result.runtime,
          segmentIndex: result.segments.length - 1,
          model: this.model.clone(),
          cns: result.cns,
          otu: result.otu
        }
      }
    }

    // Tissue state and depth planBailout replays the dive from
    result.bailoutPoint = bailoutPoint

    // CCR ascent with decompression, cut at every planned loop flush.
    //
    // A flush is not a dive level: it lives here, inside the ascent, so the TTS
    // keeps its definition - the time from the end of the last segment to the
    // surface - and naturally includes the minutes spent flushing, which are
    // minutes of the ascent.
    //
    // Each leg is a normal decompression pass floored at the flush depth. That
    // reuses the very machinery a multilevel profile uses to descend onto a
    // shallower level: the pass holds whatever stops are owed before the floor
    // is reachable, so a flush asked for at 30 m under a 36 m ceiling is not
    // moved, it simply waits for the ceiling to clear it.
    const ascentStart = currentDepth
    let decoTime = 0
    const runLeg = (floorDepth) => {
      const leg = this.decoPassCCR(currentDepth, result.maxDepth, ccr, currentDiluent,
        gfLow, gfHigh, result.runtime, gfReference, floorDepth)
      result.segments.push(...leg.segments)
      result.stops.push(...leg.stops)
      result.warnings.push(...leg.warnings)
      result.runtime = leg.runtime
      result.cns += leg.cns
      result.otu += leg.otu
      decoTime += leg.decoTime
      currentDepth = leg.endDepth
      return leg
    }

    for (const flush of flushes) {
      if (flush.depth >= ascentStart) {
        result.warnings.push(this.flushNotReachedWarning(flush, ascentStart, 'ccrPass'))
        continue
      }
      const leg = runLeg(flush.depth)
      gfReference = Math.max(gfReference, leg.gfReferenceDepth)
      if (currentDepth > flush.depth + DivePlanner.CEILING_TOLERANCE_M) {
        // The leg gave up before reaching the floor (runtime or step limit,
        // already reported by decoPassCCR). Flushing at the wrong depth would
        // be worse than not flushing, so the flush is dropped and said so.
        result.warnings.push(this.flushNotReachedWarning(flush, currentDepth, 'ccrPass'))
        continue
      }
      currentDiluent = this.performFlush(currentDepth, flush, ccr, currentDiluent,
        gfLow, gfHigh, gfReference, result, 'ccrPass')
      // Minutes spent flushing are minutes of the ascent, so they belong to the
      // time to surface exactly like a stop does.
      decoTime += flush.time
    }

    const finalLeg = runLeg(0)

    result.tts = Math.ceil(decoTime)
    // Depth the gradient factor equals GF Low at: the deepest ceiling of the dive
    result.gfReferenceDepth = Math.max(gfReference, finalLeg.gfReferenceDepth)

    return result
  }

  // Plan bailout at the worst moment of the dive: the end of the deepest
  // bottom segment, where the open circuit ascent is the longest and the gas
  // demand the highest. ccrPass records that point (depth, runtime and tissue
  // state) as ccrPlan.bailoutPoint; on a single level dive it is the end of
  // the bottom time, which is what this has always computed.
  planBailout({ ccrPlan, bailoutGases, gfLow = 0.30, gfHigh = 0.85, rmvDeco = 15, rmvStress = 30, stressUntil = 'first_stop', bailDelay = 2 }) {
    // The bailout replays the CCR dive on open circuit: without the CCR plan
    // segments there is nothing to bail out from.
    if (!ccrPlan || !Array.isArray(ccrPlan.segments) || ccrPlan.segments.length === 0) {
      console.error('planBailout: the CCR plan carries no segment (ccrPlan=%o)', ccrPlan)
      throw new PlanError(PLAN_ERROR_CODES.NO_SEGMENTS, { context: 'planBailout' })
    }
    this.validateGradientFactors(gfLow, gfHigh, 'planBailout')
    // A missing or empty bailout list used to produce a silent empty plan
    if (!Array.isArray(bailoutGases) || bailoutGases.length === 0) {
      console.error('planBailout: no bailout gas available for a %om CCR dive (bailoutGases=%o)',
        ccrPlan.maxDepth, bailoutGases)
      throw new PlanError(PLAN_ERROR_CODES.NO_BAILOUT_GAS, { context: 'planBailout', maxDepth: ccrPlan.maxDepth })
    }
    this.validateGases(bailoutGases, 'planBailout')

    // The bail delay is planned minutes at the bottom on open circuit, at the
    // stress RMV: the most expensive minutes of the whole dive. A NaN (emptied
    // settings field) or a negative value used to pass the `> 0` test below
    // and silently drop the phase, so the bailout was planned as if the diver
    // left the bottom the instant the loop failed.
    if (!Number.isFinite(bailDelay) || bailDelay < 0) {
      console.error('planBailout: invalid bailDelay (%o), expected a finite value >= 0 min', bailDelay)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_CONFIG, { context: 'planBailout', setting: 'bailDelay', value: bailDelay })
    }

    debugPlanner('planBailout: %d gases, GF %o/%o, bailDelay=%dmin', bailoutGases.length, Math.round(gfLow * 100), Math.round(gfHigh * 100), bailDelay)

    // Bailout point recorded by ccrPass. A CCR plan that carries none was not
    // produced by this planner, and the only tissue state at hand is the one
    // this.model holds after planCCR: the state at the surface. A bailout
    // replayed from it used to be computed, with a log, on tissues that had
    // already offgased the whole ascent - shorter than the real one by the
    // whole decompression. Refused instead: the plan screen recomputes the
    // CCR plan before every bailout, so only a foreign plan gets here.
    const bailoutPoint = ccrPlan.bailoutPoint
    if (!bailoutPoint || !bailoutPoint.model || typeof bailoutPoint.model.clone !== 'function') {
      console.error('planBailout: the CCR plan carries no usable bailout point (%o), refusing to replay it from the surface state',
        bailoutPoint)
      throw new PlanError(PLAN_ERROR_CODES.NO_BAILOUT_POINT, { context: 'planBailout', maxDepth: ccrPlan.maxDepth })
    }

    // Tissue state the loop was lost with, and the segment it happened on:
    // both come from the same moment of the dive, which is the whole point.
    const bailoutModel = bailoutPoint.model.clone()
    const bailoutSegment = ccrPlan.segments[bailoutPoint.segmentIndex]
    if (!bailoutSegment) {
      console.error('planBailout: the bailout point names segment #%o, which the CCR plan does not carry', bailoutPoint.segmentIndex)
      throw new PlanError(PLAN_ERROR_CODES.NO_BAILOUT_POINT, { context: 'planBailout', segmentIndex: bailoutPoint.segmentIndex })
    }

    const result = {
      segments: [],
      stops: [],
      warnings: [],
      tts: 0,
      cns: 0,
      otu: 0,
      runtime: 0,
      runtimeOffset: bailoutPoint.runtime, // Time already spent in CCR before bailout
      gasConsumption: [],
      // CCR phase info for display
      // The loop mix travels with it: the row displayed above the bailout table
      // is where the diver compares what he was breathing to what he switches
      // to, and density is the figure that moves most across that switch.
      ccrPhase: bailoutSegment ? {
        depth: bailoutSegment.depth,
        runtime: bailoutSegment.runtime,
        gas: bailoutSegment.gas,
        setpoint: bailoutSegment.setpoint,
        ppO2: bailoutSegment.ppO2,
        end: bailoutSegment.end,
        fO2: bailoutSegment.fO2,
        fHe: bailoutSegment.fHe
      } : null
    }

    // Normalize gases (fN2 recomputed, missing display names generated)
    const gases = bailoutGases.map(g => Gas.normalizeMix(g))

    // Start the bailout where the loop was lost, not at the deepest point the
    // dive ever reached: on a multilevel profile the two are the same moment
    // only because the bailout point is taken at the deepest level.
    const maxDepth = ccrPlan.maxDepth
    let currentDepth = bailoutPoint.depth

    // Use cloned model for bailout calculation
    const originalModel = this.model
    this.model = bailoutModel

    // Same swap for the series: the bailout belongs to the series of the plan
    // it replays, not to whatever this instance computed last (see
    // seriesOfPlan). Both are restored together at the end of the method.
    const originalCarriedOver = this.carriedOver
    this.carriedOver = DivePlanner.seriesOfPlan(ccrPlan, this.carriedOver, 'planBailout')

    // Cylinders the diver carries, followed from the first bailout breath to
    // the surface. The bailout is the very place a cylinder runs dry: it is
    // planned on the worst moment of the dive and pays a stress RMV at depth.
    this.gasBudget = new GasBudget(gases, {
      rmvDeco,
      rmvStress,
      stressUntil,
      environment: this.environment
    })

    // Get best gas for bottom depth
    const bottomGas = Gas.bestGas(this.availableGases(gases, currentDepth, result), currentDepth, this.ppO2Max, this.environment)

    // A bailout list whose deepest mix stops above the bottom is the case this
    // warning exists for: the loop is lost at 40 m and the deco bottles only
    // reach 22 m. The plan is still computed - the diver has to get out - but
    // the first breath it prescribes is past the MOD of everything he carries.
    const bottomGasAboveMOD = this.gasAboveMODWarning(bottomGas, currentDepth, this.ppO2Max, 'planBailout')
    if (bottomGasAboveMOD) result.warnings.push(bottomGasAboveMOD)

    // Bail delay segment: time at depth before ascending
    if (bailDelay > 0) {
      this.loadTissuesGuarded(this.model, currentDepth, bailDelay, bottomGas, 'planBailout delay')
      result.runtime += bailDelay
      const ppO2 = Gas.ppO2(bottomGas.fO2, currentDepth, this.environment)
      result.cns += Gas.calcCNS(ppO2, bailDelay)
      result.otu += Gas.calcOTU(ppO2, bailDelay)

      // Get tissue info at end of bail delay
      const tissueInfo = this.model.getLeadingTissueInfo(gfLow, gfHigh, currentDepth, null)
      const compartments = this.model.getCompartmentState(currentDepth)
      const ceiling = this.model.ceiling(gfLow, gfHigh, currentDepth, null)

      result.segments.push({
        type: 'bottom',
        depth: currentDepth,
        time: bailDelay,
        runtime: result.runtime,
        gas: bottomGas.name,
        fO2: bottomGas.fO2,
        fHe: bottomGas.fHe || 0,
        ppO2: ppO2,
        end: Gas.calcEND(currentDepth, bottomGas.fO2, bottomGas.fHe || 0, this.environment),
        isBailDelay: true,
        tissueInfo,
        compartments,
        ceiling
      })
      this.gasBudget.charge(result.segments[result.segments.length - 1])
    }

    // Everything spent so far is what each replayed decompression pass starts
    // from: resolveGfReference runs the pass up to five times, and a budget
    // that kept adding up would empty the cylinders on its own.
    this.gasBudget.markBaseline()

    // Calculate OC deco from current depth (includes full ascent with gas switches)
    const runtimeBeforeDeco = result.runtime
    // The ascent starts on the bail gas: the switch to the first deco gas, when
    // one reaches this depth under the deco limit, is written as a row after
    // the bail delay instead of silently replacing the mix the table just
    // printed the diver bailing on.
    const decoResult = this.calculateDeco(currentDepth, maxDepth, gases, gfLow, gfHigh, result.runtime, bottomGas)

    result.segments.push(...decoResult.segments)
    result.stops = decoResult.stops
    result.runtime = decoResult.runtime
    result.cns += decoResult.cns
    result.otu += decoResult.otu
    result.warnings.push(...decoResult.warnings)
    result.tts = Math.ceil(decoResult.runtime - runtimeBeforeDeco)
    // Depth where the gradient factor equals GF Low, i.e. the first stop held
    result.gfReferenceDepth = decoResult.gfReferenceDepth

    // Oxygen exposure the bailout ascent adds to: what the series carried into
    // the CCR dive, plus what that dive accumulated up to the moment the loop
    // was lost. The fallback bailout point above carries no exposure of its
    // own, so the whole CCR dive is used - that is the exposure which goes
    // with the tissue state it falls back on.
    const exposureAtBailout = Number.isFinite(bailoutPoint.cns) && Number.isFinite(bailoutPoint.otu)
      ? { cns: bailoutPoint.cns, otu: bailoutPoint.otu }
      : { cns: ccrPlan.cns || 0, otu: ccrPlan.otu || 0 }

    this.fillExposureTotals(result, exposureAtBailout)

    // Calculate gas consumption for bailout with stress RMV
    result.gasConsumption = this.calculateGasConsumption(result.segments, gases, rmvDeco, rmvStress, stressUntil)
    result.warnings.push(...this.gasInsufficientWarnings(result.gasConsumption, 'planBailout'))

    // Read the surface interval before the restore below: the bailout ascent
    // is what the diver actually surfaces from, the original model still sits
    // at the start of the CCR dive.
    this.fillSurfaceIntervalInfo(result)

    // Restore original model and series
    this.model = originalModel
    this.carriedOver = originalCarriedOver
    this.gasBudget = null

    return result
  }

  // Next stop level strictly above currentDepth, snapped back onto the
  // decoStopInterval grid and never shallower than lastStopDepth.
  //
  // Plain subtraction is not enough: with gasSwitchAtStop disabled the ascent
  // stops at the exact MOD of the next gas, which is not on the grid (pure O2
  // at 1.6 bar sits at 5 m). Subtracting the interval from there would walk the
  // whole remaining ascent off-grid and, worse, place a stop at 2 m, below the
  // configured last stop. Snapping restores the grid at the first opportunity.
  nextGridDepthAbove(currentDepth) {
    const grid = Math.ceil(currentDepth / this.decoStopInterval - 1) * this.decoStopInterval
    return Math.max(this.lastStopDepth, grid)
  }

  // nextGridDepthAbove bounded by the level the ascent is heading for
  gridDepthAbove(currentDepth, floorDepth = 0) {
    return Math.max(floorDepth, this.nextGridDepthAbove(currentDepth))
  }

  // Depth under which the ascent has reached its destination: the configured
  // last stop when surfacing, the level being joined when it is deeper.
  ascentFloor(floorDepth = 0) {
    return Math.max(this.lastStopDepth, floorDepth)
  }

  // Accumulate CNS and OTU exposure on a result object
  accumulateExposure(result, ppO2, time) {
    result.cns += Gas.calcCNS(ppO2, time)
    result.otu += Gas.calcOTU(ppO2, time)
  }

  // Surface interval information of a finished plan, in minutes.
  // Must be called while this.model still holds the state the diver leaves the
  // water with, i.e. after the ascent and before any model restore.
  //   desatTime: full desaturation at the altitude of the dive site, computed
  //              by the model, null when it did not settle (logged there)
  //   noFlyTime: DAN recommendation before flying, never a computed value
  //   decoObligation: whether the dive holds a real decompression obligation
  //   noFlyReason: which DAN case the reported delay comes from
  //   noFlyMinimum: whether that delay is a floor DAN asks to exceed
  //   heliumInDive, heliumSeries: helium breathed on this dive, and on the series
  //   tissuesAtSurface: the state the next dive of the series starts from
  fillSurfaceIntervalInfo(result) {
    result.desatTime = this.model.desaturationTime()

    // Tissue state the diver leaves the water with, serialized here because
    // this is the one place in the planner that runs with the model at exactly
    // that moment - after the ascent, before planBailout restores its own.
    result.tissuesAtSurface = this.model.toJSON()

    // Decompression obligation: any stop the ascent had to hold. A safety
    // stop (flagged `safety`) is the diver's choice, not an obligation, and
    // must not push a no-stop dive onto the 18 h no-fly floor.
    result.decoObligation = result.stops.some(s => !s.safety)

    // Helium breathed on this dive, then on the series it belongs to. The DAN
    // case covers "diving involving ... heliox and trimix", i.e. the diving that
    // precedes the flight and not the last dive of it.
    result.heliumInDive = DivePlanner.usesHelium(result.segments)
    result.heliumSeries = result.heliumInDive || this.carriedOver.helium === true

    // Which of the three DAN cases this plan falls under. Decompression comes
    // first because it is the strongest statement about this very dive, then
    // helium - both are the third case, both report a floor - then the
    // repetitive case, which is a firm 18 h.
    if (result.decoObligation) {
      result.noFlyReason = DivePlanner.NO_FLY_REASONS.DECO
    } else if (result.heliumSeries) {
      result.noFlyReason = DivePlanner.NO_FLY_REASONS.HELIUM
    } else if (this.carriedOver.repetitive) {
      result.noFlyReason = DivePlanner.NO_FLY_REASONS.REPETITIVE
    } else {
      result.noFlyReason = DivePlanner.NO_FLY_REASONS.NO_STOP
    }

    // The third case is "substantially longer than 18 hours": the delay below is
    // then a floor and not the recommendation, and noFlyMinimum is what tells
    // the views to print it as one.
    result.noFlyMinimum = result.noFlyReason === DivePlanner.NO_FLY_REASONS.DECO ||
      result.noFlyReason === DivePlanner.NO_FLY_REASONS.HELIUM
    result.noFlyTime = DivePlanner.noFlyDelay(result.noFlyReason)

    debugPlanner('surface interval: desat=%o min, noFly=%o min (reason %s, minimum %o, deco obligation %o, helium %o)',
      result.desatTime, result.noFlyTime, result.noFlyReason, result.noFlyMinimum,
      result.decoObligation, result.heliumSeries)
  }

  // Get leading tissue info, compartment state, and ceiling at a given depth
  getTissueSnapshot(depth, gfLow, gfHigh, firstStopDepth) {
    return {
      tissueInfo: this.model.getLeadingTissueInfo(gfLow, gfHigh, depth, firstStopDepth),
      compartments: this.model.getCompartmentState(depth),
      ceiling: this.model.ceiling(gfLow, gfHigh, depth, firstStopDepth)
    }
  }

  // Check for gas switch at current depth. If a better gas is available,
  // load tissues during switch time and push a gasChange segment.
  // Includes ppO2, END and tissue snapshot so the plan table can display them.
  // Returns the (possibly new) active gas.
  handleGasSwitchAtStop(currentDepth, gases, activeGas, result, gfLow, gfHigh, firstStopDepth) {
    const bestGas = Gas.bestGas(this.availableGases(gases, currentDepth, result), currentDepth, this.ppO2Deco, this.environment)
    return this.switchToGas(currentDepth, bestGas, activeGas, result, gfLow, gfHigh, firstStopDepth)
  }

  // Put `toGas` in the diver's mouth at currentDepth: hold the switch for
  // gasSwitchDuration on the new mix, count its exposure, and push the
  // gasChange row the tables print. Returns the gas now breathed, i.e.
  // activeGas itself when nothing changes.
  //
  // Every change of mix of an open circuit plan goes through here, the ones
  // the ascent decides (handleGasSwitchAtStop) and the ones the diver wrote
  // segment by segment. A change that skipped this function used to leave no
  // row at all: the plan read "bottom on air, ascent on EAN50", with no line
  // saying when the regulator changed hands and no minute counted for it.
  switchToGas(currentDepth, toGas, activeGas, result, gfLow, gfHigh, firstStopDepth) {
    if (!toGas || toGas.name === activeGas.name) return activeGas

    const switchTime = this.gasSwitchDuration
    const ppO2 = Gas.ppO2(toGas.fO2, currentDepth, this.environment)
    if (switchTime > 0) {
      this.loadTissuesGuarded(this.model, currentDepth, switchTime, toGas, 'switchToGas')
      result.runtime += switchTime
      this.accumulateExposure(result, ppO2, switchTime)
    }

    // Capture tissue state after switch for plan table display
    const { tissueInfo, compartments, ceiling } = this.getTissueSnapshot(currentDepth, gfLow, gfHigh, firstStopDepth)

    result.segments.push({
      type: 'gasChange',
      depth: currentDepth,
      gas: toGas.name,
      gasName: toGas.name,
      fO2: toGas.fO2,
      fHe: toGas.fHe || 0,
      time: switchTime,
      previousGas: activeGas.name,
      runtime: result.runtime,
      ppO2,
      end: Gas.calcEND(currentDepth, toGas.fO2, toGas.fHe || 0, this.environment),
      tissueInfo,
      compartments,
      ceiling
    })
    this.gasBudget?.charge(result.segments[result.segments.length - 1])
    debugPlannerSegments('gas switch at %om: %s -> %s (%o min)', currentDepth, activeGas.name, toGas.name, switchTime)
    return toGas
  }

  // Gas object behind the last segment of a pass that names one, or `fallback`
  // when the pass produced no breathing segment. A decompression pass may
  // switch mixes on its way to a level, and the level then starts on the last
  // mix it put in the diver's mouth, not on the one he left the bottom with.
  gasBreathedAtEnd(segments, gases, fallback) {
    for (let i = segments.length - 1; i >= 0; i--) {
      const name = segments[i].gas
      if (!name) continue
      const gas = gases.find(g => g.name === name)
      if (gas) return gas
      console.error('gasBreathedAtEnd: the pass ends on %o, which is not in the gas list, keeping %o', name, fallback?.name)
      return fallback
    }
    return fallback
  }

  // Build a stop time truncation warning, log it, and return it.
  // Called when findStopTime hit MAX_STOP_TIME without clearing the ceiling:
  // the stop is cut short, so the plan is not a complete decompression.
  stopTimeTruncatedWarning(depth, time, context) {
    console.error('%s: stop at %om truncated at %o min (MAX_STOP_TIME=%o), the ceiling was still not cleared',
      context, depth, time, DivePlanner.MAX_STOP_TIME)
    return { code: PLAN_WARNING_CODES.STOP_TIME_TRUNCATED, details: { depth, time } }
  }

  // Build a warning for a gas held deeper than its MOD, log it, and return it.
  // Returns null when the gas does reach the depth, which is the normal case.
  //
  // Gas.bestGas never fails: when nothing in the list reaches the depth it
  // returns the least hyperoxic mix rather than the first one declared, and
  // the plan stays computable. That silent substitution is exactly what has to
  // be said out loud - the diver carries no gas breathable where this plan
  // puts him, and the mix shown in the table is the engine's least bad choice,
  // not one he planned for.
  gasAboveMODWarning(gas, depth, ppO2Max, context) {
    if (!gas || Gas.reachesDepth(gas, depth, ppO2Max, this.environment)) return null

    const mod = Gas.modOf(gas, ppO2Max, this.environment)
    const ppO2 = Gas.ppO2(gas.fO2, depth, this.environment)
    // The limit actually enforced, which is the stricter of the phase one and
    // the one written on the gas. It belongs in the message: the Gases view
    // and the gas list of the exports print the MOD of the gas alone, so
    // without it the diver reads two different MODs for one mix and no reason
    // for the gap.
    const limit = Gas.ppO2LimitOf(gas, ppO2Max)
    console.error('%s: no carried gas reaches %om under a %o bar ppO2 limit, %s is held there (MOD %om, ppO2 %o bar)',
      context, depth, limit, gas.name, mod, ppO2.toFixed(2))

    return {
      code: PLAN_WARNING_CODES.GAS_ABOVE_MOD,
      details: {
        context,
        gas: gas.name,
        depth: Math.round(depth * 10) / 10,
        mod,
        limit,
        ppO2: ppO2.toFixed(2)
      }
    }
  }

  // Build a warning for a cylinder that ran dry, log it, and return it.
  // Returns null when the mix has already been reported, so the four places a
  // cylinder can die - mid stop, before a stop, mid ascent, before an ascent -
  // say it once between them.
  //
  // The plan stays computable: availableGases stops offering the empty mix and
  // the ascent goes on with what is left. But the table then prints a plain
  // 'gas change' line at a depth nobody planned, and the switch can even go
  // towards a LEANER mix while ascending, which no deco strategy would ever
  // prescribe. Read without this warning, a forced switch is indistinguishable
  // from a deliberate one.
  gasSwitchOnEmptyWarning(gas, depth, context) {
    if (!gas || !this.gasBudget?.tracked) return null
    const key = GasBudget.mixKey(gas)
    if (this.gasBudget.announced.has(key)) return null
    this.gasBudget.announced.add(key)

    const at = Math.round(depth * 10) / 10
    console.error('%s: %s runs dry at %om, the ascent switches to whatever is left',
      context, gas.name, at)
    return { code: PLAN_WARNING_CODES.GAS_SWITCH_ON_EMPTY, details: { gas: gas.name, depth: at } }
  }

  // Warnings for every line of the requirement table that is short, logged
  // and returned. The verdict of the table stays where it is; this carries it
  // to result.warnings, so the alert block, the day view and the sheet
  // metadata read it too - an open circuit plan on a cylinder too small used
  // to leave every one of them silent. Only for cylinders the diver described:
  // a line judged against the default 12 L / 200 bar says nothing about what
  // is carried, and a warning on it would fire on every plan whose gases have
  // no volume, which is every reference scenario and many real plans.
  gasInsufficientWarnings(rows, context) {
    const warnings = []
    for (const row of (Array.isArray(rows) ? rows : [])) {
      if (!row.insufficient || !row.declared) continue
      const missing = Math.ceil(row.liters - row.tankCapacity)
      console.error('%s: the %s cylinders hold %o L and the plan breathes %o L of that mix, short by %o L',
        context, row.name, row.tankCapacity, row.liters, missing)
      warnings.push({
        code: PLAN_WARNING_CODES.GAS_INSUFFICIENT,
        details: { context, gas: row.name, capacity: Math.round(row.tankCapacity), liters: row.liters, missing }
      })
    }
    return warnings
  }

  // Build a runtime limit warning, log it, and return it.
  // Called when a deco loop was interrupted at MAX_RUNTIME.
  runtimeLimitWarning(runtime, depth, context) {
    console.error('%s: runtime limit reached (%o min > MAX_RUNTIME=%o) at %om, deco loop interrupted',
      context, runtime, DivePlanner.MAX_RUNTIME, depth)
    return { code: PLAN_WARNING_CODES.RUNTIME_LIMIT, details: { runtime } }
  }

  // ---------------------------------------------------------------------------
  // Ascent machinery, shared by the open circuit and the rebreather passes
  // ---------------------------------------------------------------------------

  // Water column, in bar, the GF Low reference is never shallower than.
  //
  // The gradient factor interpolation is anchored on the deepest GF Low
  // ceiling the tissues produced so far during the dive, unrounded, and on GF
  // High at the surface: Subsurface's convention (core/deco.cpp,
  // gf_low_pressure_this_dive, initialised at surface + gf_low_position_min,
  // 1.0 bar). The engine used to anchor GF Low on the first stop held ON THE
  // 3 M GRID, up to a notch deeper than the ceiling itself; every shallower
  // stop then got a more permissive gradient factor, and on the profiles where
  // the rounding was worst the diver surfaced before Subsurface would let him
  // (-2.3 min on air 40 m / 30 min in the Dead Sea).
  static GF_LOW_MIN_PRESSURE = 1.0

  gfReferenceFloor() {
    return DivePlanner.GF_LOW_MIN_PRESSURE / Gas.specificWeight(this.environment.waterType)
  }

  // A reference handed in by a caller, made usable: the floor when there is
  // none, refused (logged, floor) when it is not a finite depth. A NaN here
  // would turn every ceiling into NaN and the plan into a dive with no stop.
  usableReference(value, context) {
    const floor = this.gfReferenceFloor()
    if (value === null || value === undefined) return floor
    if (!Number.isFinite(value) || value < 0) {
      console.error('%s: invalid GF reference depth (%o), starting from the %om floor', context, value, floor.toFixed(1))
      return floor
    }
    return Math.max(floor, value)
  }

  // Running maximum of the reference, read on this.model at `depth`
  noteGfReference(reference, depth, gfLow, gfHigh) {
    return Math.max(reference, this.model.ceiling(gfLow, gfHigh, depth, null))
  }

  // Minutes a leg from `from` up to `to` takes at the rate of its phase
  legTime(from, to, inDecoZone) {
    return (from - to) / this.getAscentRate(to, inDecoZone)
  }

  // Ceiling a leg has to satisfy on arrival: the self consistent ceiling of
  // the tissues under the reference of the dive (Buhlmann.ceiling)
  arrivalCeiling(model, to, gfLow, gfHigh, reference) {
    return model.ceiling(gfLow, gfHigh, to, reference)
  }

  // May the diver leave `from` for `to` right now? Subsurface's trial_ascent
  // for Buhlmann (core/planner.cpp): the leg is played on a copy of the
  // tissues, and the self consistent ceiling at the end of it has to clear
  // the arrival depth. The off-gassing of the leg is therefore credited, and a
  // slow tissue still on-gassing during a long free ascent is charged: the
  // engine used to read the ceiling at the stop, under the gradient factor of
  // the next level, and never play the leg, so the arrival row of the plan
  // could report a ceiling below the diver while nothing had stopped him.
  // A 3 m leg is short enough for its end to be the binding point, which is
  // what Subsurface checks every two seconds.
  //
  // `breather` is how the tissues are loaded on this circuit:
  //   stay(model, depth, minutes)      minutes held at a depth
  //   leg(model, from, to, minutes)    a leg between two depths
  //   row(depth)                       the gas fields of a row held at depth
  //   ascentRow(from, to)              the gas fields of an ascent row
  legClears(from, to, breather, gfLow, gfHigh, reference, inDecoZone) {
    return this.legClearsFrom(this.model, from, to, breather, gfLow, gfHigh, reference, inDecoZone)
  }

  legClearsFrom(model, from, to, breather, gfLow, gfHigh, reference, inDecoZone) {
    // No tolerance here: CEILING_TOLERANCE_M is for reporting a plan, not for
    // leaving a stop. Ten centimetres of slack at a 3 m stop, where the
    // ceiling drops a few centimetres a minute, is a minute of stop gone.
    const trial = model.clone()
    breather.leg(trial, from, to, this.legTime(from, to, inDecoZone))
    return this.arrivalCeiling(trial, to, gfLow, gfHigh, reference) <= to
  }

  // Whole minutes to hold at `depth` before the leg to `target` clears.
  // Returns { time, truncated }: truncated when MAX_STOP_TIME went by with the
  // leg still not clearing, i.e. the plan does not fully decompress the diver.
  //
  // Tested BEFORE the first minute: the loop used to load a minute then test,
  // so it could never answer 0. Every level evaluated after the first stop
  // cost a minute, and every plan ended with a minute at the last stop even
  // when no decompression was owed. Against Subsurface that was 33 one minute
  // stops on 50 profiles at depths where it holds none, and a first stop one
  // notch too deep on 30 of them.
  findStopTime(depth, target, reference, gfLow, gfHigh, breather, inDecoZone) {
    const held = this.model.clone()
    const cleared = () => this.legClearsFrom(held, depth, target, breather, gfLow, gfHigh, reference, inDecoZone)
    let time = 0
    if (cleared()) return { time, truncated: false }
    while (time < DivePlanner.MAX_STOP_TIME) {
      breather.stay(held, depth, 1)
      time++
      if (cleared()) return { time, truncated: false }
    }
    return { time, truncated: true }
  }

  // Ceiling still below the diver once a leg has been played for real: only
  // a leg forced through (truncated stop, runtime limit) can produce one, and
  // alerts.js reports it as a ceiling violation.
  ceilingBreachAtArrival(from, to, gfLow, gfHigh, reference, context) {
    const ceiling = this.arrivalCeiling(this.model, to, gfLow, gfHigh, reference)
    if (ceiling <= to + DivePlanner.CEILING_TOLERANCE_M) return null
    console.error('%s: arrived at %om from %om under a %om ceiling, the plan crosses it',
      context, to, from, ceiling.toFixed(1))
    return { from, depth: to, ceiling }
  }

  // Hold `minutes` at `depth` as a decompression stop: tissues, runtime,
  // exposure, the stop entry and its row. Returns the minutes held.
  pushStopSegment(result, depth, minutes, breather, gfLow, gfHigh, reference) {
    breather.stay(this.model, depth, minutes)
    result.runtime += minutes
    const row = breather.row(depth)
    this.accumulateExposure(result, row.ppO2, minutes)
    const { tissueInfo, compartments, ceiling } = this.getTissueSnapshot(depth, gfLow, gfHigh, reference)
    result.stops.push({ depth, time: Math.ceil(minutes), gas: row.gas })
    const segment = { type: 'stop', depth, time: minutes, runtime: result.runtime, ...row, tissueInfo, compartments, ceiling }
    result.segments.push(segment)
    this.gasBudget?.charge(segment)
    return minutes
  }

  // Write the legs accumulated in `pending` as one ascent row ending at `to`.
  // The free ascent to the first stop is one row whatever the number of grid
  // legs it took; between stops every leg is its own row, as it always was.
  pushAscentSegment(result, pending, to, breather, gfLow, gfHigh, reference) {
    const { tissueInfo, compartments, ceiling } = this.getTissueSnapshot(to, gfLow, gfHigh, reference)
    const segment = {
      type: 'ascent', startDepth: pending.start, endDepth: to, time: pending.time, runtime: result.runtime,
      ...breather.ascentRow(pending.start, to), tissueInfo, compartments, ceiling
    }
    if (pending.breach) segment.ceilingBreach = pending.breach
    result.segments.push(segment)
    // Not charged to the cylinder budget here: decoPass charges every leg as
    // it is played, so a cylinder running dry is seen at the leg it dies on
    // and not at the end of the whole free ascent
  }

  // Hold the safety stop of an ascent that owes no decompression stop:
  // safetyStopTime minutes at `depth`, written as a stop row flagged `safety`
  // so the tables name it for what it is and decoObligation leaves it out.
  // Nothing is held when the ascent already holds a decompression stop: the
  // last deco stop is where the diver already waits. Returns the minutes held.
  holdSafetyStop(depth, result, breather, gfLow, gfHigh, reference, context) {
    const time = this.safetyStopTime
    if (!(time > 0) || result.stops.some(s => !s.safety)) return 0

    breather.stay(this.model, depth, time)
    result.runtime += time
    const row = breather.row(depth)
    this.accumulateExposure(result, row.ppO2, time)
    const { tissueInfo, compartments, ceiling } = this.getTissueSnapshot(depth, gfLow, gfHigh, reference)

    result.stops.push({ depth, time, gas: row.gas, safety: true })
    const segment = { type: 'stop', safety: true, depth, time, runtime: result.runtime, ...row, tissueInfo, compartments, ceiling }
    result.segments.push(segment)
    this.gasBudget?.charge(segment)
    debugPlannerDeco('  %s: safety stop, %o min at %om on %s', context, time, depth, row.gas)
    return time
  }

  // Calculate OC decompression from currentDepth to surface.
  calculateDeco(currentDepth, maxDepth, gases, gfLow, gfHigh, initialRuntime = 0, entryGas = null) {
    return this.decoPass(currentDepth, maxDepth, gases, gfLow, gfHigh, initialRuntime, null, 0, entryGas)
  }

  // Open circuit ascent from currentDepth to floorDepth: 0 for the surface,
  // the depth of the next dive level when a multilevel profile owes stops
  // before reaching it. One grid level at a time, each leg played on a copy of
  // the tissues first (legClears); a leg that does not clear is a stop, held
  // for the whole minutes findStopTime says. The stop grid, the last stop rule
  // and the safety stop are all bounded by floorDepth, so the diver joins a
  // level with a cleared ceiling and no stop is placed above it.
  // result.endDepth is the depth actually reached, which differs from
  // floorDepth only when MAX_RUNTIME interrupted the loop.
  //
  // Gas: entryGas is the mix in the mouth when the pass starts (the gas of the
  // level being left, the bail gas of a bailout); without it the richest mix
  // reaching currentDepth under the deco limit is taken, silently, as the
  // historical callers expect. Every change of mix goes through switchToGas:
  // before the first move when the diver changes mix at MOD, at the first
  // stop when he changes at stops, and at every stop the best mix is taken.
  decoPass(currentDepth, maxDepth, gases, gfLow, gfHigh, initialRuntime, gfReferenceDepth, floorDepth = 0, entryGas = null) {
    debugPlannerDeco('decoPass: from %dm to %dm, maxDepth=%dm, runtime=%o', currentDepth, floorDepth, maxDepth, initialRuntime)

    const result = { segments: [], stops: [], warnings: [], runtime: initialRuntime, cns: 0, otu: 0, endDepth: currentDepth }
    let reference = this.usableReference(gfReferenceDepth, 'decoPass')
    reference = this.noteGfReference(reference, currentDepth, gfLow, gfHigh)

    let activeGas = entryGas
      ?? Gas.bestGas(this.availableGases(gases, currentDepth, result), currentDepth, this.ppO2Deco, this.environment)
    if (entryGas && !this.gasSwitchAtStop) {
      activeGas = this.handleGasSwitchAtStop(currentDepth, gases, activeGas, result, gfLow, gfHigh, reference)
    }

    const env = this.environment
    const breather = {
      stay: (model, depth, minutes) => this.loadTissuesGuarded(model, depth, minutes, activeGas, 'decoPass stop'),
      leg: (model, from, to, minutes) => this.loadTissuesAscDecGuarded(model, from, to, minutes, activeGas, 'decoPass ascent'),
      row: depth => ({
        gas: activeGas.name, fO2: activeGas.fO2, fHe: activeGas.fHe || 0,
        ppO2: Gas.ppO2(activeGas.fO2, depth, env),
        end: Gas.calcEND(depth, activeGas.fO2, activeGas.fHe || 0, env)
      }),
      ascentRow: (from, to) => ({
        gas: activeGas.name, fO2: activeGas.fO2, fHe: activeGas.fHe || 0,
        ppO2: Gas.ppO2(activeGas.fO2, (from + to) / 2, env),
        end: Gas.calcEND(to, activeGas.fO2, activeGas.fHe || 0, env)
      })
    }

    let inDecoZone = false
    let forceLeg = false
    let pending = null
    const flushPending = () => {
      if (!pending) return
      this.pushAscentSegment(result, pending, currentDepth, breather, gfLow, gfHigh, reference)
      pending = null
    }

    let guard = 0
    while (currentDepth > floorDepth) {
      // An ascent has a bounded number of steps: one per grid level, plus one
      // per gas switch. A pass that goes far past that is circling a depth it
      // cannot leave, and a browser tab frozen solid tells the diver nothing.
      if (++guard > DivePlanner.MAX_DECO_STEPS) {
        console.error('decoPass: giving up at %om after %d steps (floor %om, gas %s) - the ascent is not progressing',
          currentDepth, guard, floorDepth, activeGas?.name)
        result.warnings.push(this.runtimeLimitWarning(result.runtime, currentDepth, 'decoPass'))
        break
      }

      // Next level: one grid step, the destination once at the last stop
      let target = currentDepth <= this.ascentFloor(floorDepth) ? floorDepth : this.gridDepthAbove(currentDepth, floorDepth)

      // Changing mix at MOD: the leg stops where the next gas becomes usable.
      // `>=`: a MOD sitting exactly on the grid level heading for is a switch
      // depth all the same; zero means no gas is waiting.
      let ascToGasSwitch = false
      if (!this.gasSwitchAtStop) {
        const nextGasSwitch = Gas.nextGasStop(this.availableGases(gases, currentDepth, result), currentDepth, this.ppO2Deco, env)
        if (nextGasSwitch > 0 && nextGasSwitch >= target && nextGasSwitch < currentDepth) {
          target = nextGasSwitch
          ascToGasSwitch = true
        }
      }

      if (!forceLeg && !this.legClears(currentDepth, target, breather, gfLow, gfHigh, reference, inDecoZone)) {
        // A stop, here. The best mix for this depth first: at a stop the diver
        // changes to the richest gas he may breathe here, in both switch modes.
        flushPending()
        inDecoZone = true
        activeGas = this.handleGasSwitchAtStop(currentDepth, gases, activeGas, result, gfLow, gfHigh, reference)
        const stop = this.findStopTime(currentDepth, target, reference, gfLow, gfHigh, breather, inDecoZone)
        let stopTime = stop.time

        // A cylinder that empties in the middle of a stop does not make the
        // diver hold their breath: the stop is cut at the last WHOLE minute
        // the cylinder pays for, the loop comes back to this depth where the
        // empty cylinder is no longer offered, and the remaining minutes are
        // recomputed on the mix that takes over. Only when another mix can
        // take over here: cutting a stop with nothing to switch to would leave
        // the pass circling a depth it can never leave. A cylinder with less
        // than a minute left is drained on the spot rather than held for a
        // slice no diver times.
        if (stopTime > 0 && this.gasBudget?.tracked && this.hasReliefFor(activeGas, currentDepth, gases)) {
          const sustainable = this.gasBudget.sustainableTime(activeGas, { type: 'stop', depth: currentDepth, gas: activeGas.name, time: stopTime })
          if (sustainable < stopTime) {
            const dry = this.gasSwitchOnEmptyWarning(activeGas, currentDepth, 'calculateDeco')
            if (dry) result.warnings.push(dry)
            const wholeMinutes = Math.floor(sustainable)
            if (wholeMinutes < 1) {
              this.gasBudget.drain(activeGas)
              debugPlannerDeco('  %s drained at %dm, stop continues on the next mix', activeGas.name, currentDepth)
              continue
            }
            debugPlannerDeco('  %s runs out after %o min of the %d min stop at %dm, held %d min on it', activeGas.name, sustainable.toFixed(1), stopTime, currentDepth, wholeMinutes)
            stopTime = wholeMinutes
          }
        }

        if (stop.truncated) {
          result.warnings.push(this.stopTimeTruncatedWarning(currentDepth, stopTime, 'calculateDeco'))
          // The tissues will not clear this leg in MAX_STOP_TIME: the diver is
          // still brought up, and the row reports the ceiling it crosses
          forceLeg = true
        }
        if (stopTime > 0) this.pushStopSegment(result, currentDepth, stopTime, breather, gfLow, gfHigh, reference)
        if (!stop.truncated) continue
      }

      // The last stop is where the safety stop is held, when nothing is owed
      if (target === 0) {
        flushPending()
        this.holdSafetyStop(currentDepth, result, breather, gfLow, gfHigh, reference, 'calculateDeco')
      }

      // A cylinder that empties on the way up is breathed down to the whole
      // metre it runs out at (rounded to the deeper side: the rounding can only
      // leave gas in the cylinder), and the ascent goes on from there on the
      // next one. Charging the whole leg to a cylinder that cannot pay for it
      // printed INSUFFICIENT on configurations that pass.
      let legTarget = target
      if (this.gasBudget?.tracked && this.hasReliefFor(activeGas, currentDepth, gases)) {
        const affordable = Math.min(currentDepth, Math.ceil(this.depthWithinGasBudget(currentDepth, target, activeGas, inDecoZone)))
        if (affordable > target) {
          const dry = this.gasSwitchOnEmptyWarning(activeGas, affordable, 'calculateDeco')
          if (dry) result.warnings.push(dry)
          const sliceTime = this.legTime(currentDepth, affordable, inDecoZone)
          if (affordable >= currentDepth || sliceTime < DivePlanner.MIN_BUDGETED_SEGMENT_TIME) {
            // Not even a measurable slice left: switch here, before moving
            this.gasBudget.drain(activeGas)
            flushPending()
            activeGas = this.handleGasSwitchAtStop(currentDepth, gases, activeGas, result, gfLow, gfHigh, reference)
            debugPlannerDeco('  switched to %s at %dm, the previous cylinder could not pay the ascent', activeGas.name, currentDepth)
            continue
          }
          debugPlannerDeco('  %s runs out at %om on the way up to %om', activeGas.name, affordable, target)
          legTarget = affordable
          ascToGasSwitch = false
        }
      }

      // The leg itself, charged to the cylinder leg by leg
      const minutes = this.legTime(currentDepth, legTarget, inDecoZone)
      breather.leg(this.model, currentDepth, legTarget, minutes)
      result.runtime += minutes
      this.accumulateExposure(result, Gas.ppO2(activeGas.fO2, (currentDepth + legTarget) / 2, env), minutes)
      this.gasBudget?.charge({ type: 'ascent', startDepth: currentDepth, endDepth: legTarget, time: minutes, gas: activeGas.name, fO2: activeGas.fO2, fHe: activeGas.fHe || 0 })
      const breach = this.ceilingBreachAtArrival(currentDepth, legTarget, gfLow, gfHigh, reference, 'decoPass')
      if (!pending) pending = { start: currentDepth, time: 0, breach: null }
      pending.time += minutes
      if (breach) pending.breach = pending.breach || breach
      currentDepth = legTarget
      reference = this.noteGfReference(reference, currentDepth, gfLow, gfHigh)
      forceLeg = false

      // Between stops every leg is its own row; a change of mix closes the row
      if (inDecoZone || ascToGasSwitch || legTarget !== target) flushPending()
      if (ascToGasSwitch) {
        activeGas = this.handleGasSwitchAtStop(currentDepth, gases, activeGas, result, gfLow, gfHigh, reference)
      }

      if (result.runtime > DivePlanner.MAX_RUNTIME) {
        result.warnings.push(this.runtimeLimitWarning(result.runtime, currentDepth, 'calculateDeco'))
        break
      }
    }

    flushPending()
    result.endDepth = currentDepth
    result.gfReferenceDepth = reference
    return result
  }

  // Rebreather ascent from currentDepth to floorDepth: the same walk as
  // decoPass on the loop, at the ascent setpoint, with no gas to switch and
  // the minutes of the ascent counted in result.decoTime for the TTS.
  decoPassCCR(currentDepth, maxDepth, ccr, diluent, gfLow, gfHigh, initialRuntime, gfReferenceDepth, floorDepth = 0) {
    const result = { segments: [], stops: [], warnings: [], runtime: initialRuntime, decoTime: 0, cns: 0, otu: 0, endDepth: currentDepth }
    let reference = this.usableReference(gfReferenceDepth, 'decoPassCCR')
    reference = this.noteGfReference(reference, currentDepth, gfLow, gfHigh)

    const phase = CCR.PHASES.ASCENT
    const equivAt = depth => ccr.getEquivalentGas(depth, phase)
    const breather = {
      stay: (model, depth, minutes) => this.loadTissuesGuarded(model, depth, minutes, equivAt(depth), 'decoPassCCR stop'),
      leg: (model, from, to, minutes) => this.loadCCRLeg(model, ccr, from, to, minutes, phase, 'decoPassCCR ascent'),
      row: depth => {
        const loop = ccr.getLoopGasMix(depth, phase)
        return { gas: diluent.name, fO2: loop.fO2, fHe: loop.fHe, ppO2: equivAt(depth).ppO2, setpoint: ccr.heldSetpoint(depth, phase), end: ccr.getEND(depth, phase) }
      },
      ascentRow: (from, to) => {
        const loop = ccr.getLoopGasMix(to, phase)
        return { gas: diluent.name, fO2: loop.fO2, fHe: loop.fHe, ppO2: equivAt((from + to) / 2).ppO2, setpoint: ccr.heldSetpoint((from + to) / 2, phase), end: ccr.getEND(to, phase) }
      }
    }

    let inDecoZone = false
    let forceLeg = false
    let pending = null
    const flushPending = () => {
      if (!pending) return
      this.pushAscentSegment(result, pending, currentDepth, breather, gfLow, gfHigh, reference)
      pending = null
    }

    let guard = 0
    while (currentDepth > floorDepth) {
      if (++guard > DivePlanner.MAX_DECO_STEPS) {
        console.error('decoPassCCR: giving up at %om after %d steps (floor %om) - the ascent is not progressing', currentDepth, guard, floorDepth)
        result.warnings.push(this.runtimeLimitWarning(result.runtime, currentDepth, 'decoPassCCR'))
        break
      }
      const target = currentDepth <= this.ascentFloor(floorDepth) ? floorDepth : this.gridDepthAbove(currentDepth, floorDepth)

      if (!forceLeg && !this.legClears(currentDepth, target, breather, gfLow, gfHigh, reference, inDecoZone)) {
        flushPending()
        inDecoZone = true
        const stop = this.findStopTime(currentDepth, target, reference, gfLow, gfHigh, breather, inDecoZone)
        if (stop.truncated) {
          result.warnings.push(this.stopTimeTruncatedWarning(currentDepth, stop.time, 'calculateDecoCCR'))
          forceLeg = true
        }
        if (stop.time > 0) result.decoTime += this.pushStopSegment(result, currentDepth, stop.time, breather, gfLow, gfHigh, reference)
        if (!stop.truncated) continue
      }

      if (target === 0) {
        flushPending()
        result.decoTime += this.holdSafetyStop(currentDepth, result, breather, gfLow, gfHigh, reference, 'calculateDecoCCR')
      }

      const minutes = this.legTime(currentDepth, target, inDecoZone)
      breather.leg(this.model, currentDepth, target, minutes)
      result.runtime += minutes
      result.decoTime += minutes
      this.accumulateExposure(result, equivAt((currentDepth + target) / 2).ppO2, minutes)
      const breach = this.ceilingBreachAtArrival(currentDepth, target, gfLow, gfHigh, reference, 'decoPassCCR')
      if (!pending) pending = { start: currentDepth, time: 0, breach: null }
      pending.time += minutes
      if (breach) pending.breach = pending.breach || breach
      currentDepth = target
      reference = this.noteGfReference(reference, currentDepth, gfLow, gfHigh)
      forceLeg = false
      if (inDecoZone) flushPending()

      if (result.runtime > DivePlanner.MAX_RUNTIME) {
        result.warnings.push(this.runtimeLimitWarning(result.runtime, currentDepth, 'calculateDecoCCR'))
        break
      }
    }

    flushPending()
    result.endDepth = currentDepth
    result.gfReferenceDepth = reference
    debugPlannerDeco('decoPassCCR complete: %d stops, total runtime=%o', result.stops.length, result.runtime.toFixed(1))
    return result
  }

  // Calculate gas consumption for segments
  // rmvStress: higher RMV during stress phase (bailout)
  // stressUntil: 'first_stop', 'gas_switch', or 'surface'
  calculateGasConsumption(segments, gases, rmv, rmvStress = null, stressUntil = 'first_stop') {
    const consumption = {}

    // Initialize consumption for each gas (see calculateGasConsumptionOC for
    // what firstUse is for)
    for (const gas of gases) {
      consumption[gas.name] = {
        gas: gas,
        liters: 0,
        maxDepth: 0,
        firstUse: Infinity
      }
    }

    // Track stress phase
    let inStressPhase = rmvStress !== null
    const initialGas = segments.length > 0 ? segments[0].gas : null

    // Calculate consumption per segment
    for (const [index, segment] of segments.entries()) {
      const gasName = segment.gas
      if (!consumption[gasName]) continue
      // Determine if stress phase ends at this segment
      if (inStressPhase && stressUntil !== 'surface') {
        if (stressUntil === 'first_stop' && segment.type === 'stop') {
          inStressPhase = false
        } else if (stressUntil === 'gas_switch' && segment.gas !== initialGas) {
          inStressPhase = false
        }
      }

      const currentRmv = inStressPhase ? rmvStress : rmv
      const liters = DivePlanner.litersFor(segment, currentRmv, this.environment)
      if (!(liters > 0)) continue
      consumption[gasName].liters += liters
      consumption[gasName].maxDepth = Math.max(consumption[gasName].maxDepth, DivePlanner.segmentDeepestPoint(segment))
      consumption[gasName].firstUse = Math.min(consumption[gasName].firstUse, index)
    }

    return this.summarizeGasConsumption(consumption, gases)
  }

}
